/* eslint-disable spire/no-node-fetch */
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getSession } from "@insite/client-framework/Services/SessionService";
import { GetCurrentCountriesApiParameter } from "@insite/client-framework/Services/WebsiteService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCountriesDataView } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCountries";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { formatPriceRangeFacet } from "@insite/content-library/Widgets/ProductList/ProductListPriceFilters";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import FileUpload from "@insite/mobius/FileUpload";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import { UnorderedList } from "@insite/mobius/Lists";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Select, { SelectProps } from "@insite/mobius/Select";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatPhoneNumber } from "@TKCCP/Common/Utilities/formatPhoneNumber";
import { formatZipCode } from "@TKCCP/Common/Utilities/formatZipCode";
import { LoadCustomerDetailParameter } from "@TKCCP/Services/tkccpCustomerRegistrationService";
import { getCustomSettings } from "@TKCCP/Store/Context/ContextSelector";
import addCustomer from "@TKCCP/Store/Data/CustomerRegistration/Handlers/AddCustomerRegistration";
import loadCustomerDetails from "@TKCCP/Store/Pages/CustomerDetail/Handlers/LoadCustomerDetails";
import loadCustomerAttribute from "@TKCCP/Store/Pages/CustomerRegistration/Handlers/LoadCustomerAttribute";
import { themeColor } from "@TKCCP/Styles/variables";
import { CustomerAttributeModel, NewCustomerModel } from "@TKCCP/Types/ApiModels";
import replace from "lodash/replace";
import React, { ChangeEvent, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const getCountriesApiParameter: GetCurrentCountriesApiParameter = {
    expand: ["states"],
};

export interface AddCustomerApiParameter {
    newCustomerModel: NewCustomerModel;
    properties: { [key: string]: string };
}

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const parsedQuery = parseQueryString<{ referencenumber?: string }>(search);
    return {
        countriesState: getCountriesDataView(state, getCountriesApiParameter),
        securityQuestion: getCustomSettings(state)?.securityQuestion,
        creditApplicationDocumentPath: getCustomSettings(state)?.creditApplicationDocumentPath,
        referenceNumber: parsedQuery.referencenumber,
        userRole: state.context.session.userRoles,
        userName: state.context.session.userName,
    };
};

const mapDispatchToProps = {
    loadCountries,
    addCustomer,
    loadCustomerDetails,
    loadCustomerAttribute,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasToasterContext;

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    submitBtnGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    userPurchaseGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    accountManagerGridItem?: GridItemProps;
    accountManagerGridContainer?: GridContainerProps;
    userNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    nextBtnGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    nextButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    documentUploadTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    heading?: TypographyProps;
    userRadioBtn?: InjectableCss;
    userOthersGridItem?: GridItemProps;
    userGridBillingItem?: GridItemProps;
    userGridShippingItem?: GridItemProps;
    userRadioBtnGridItem?: GridItemProps;
    userNameEmptyGridItem?: GridItemProps;
    downloadGridItem?: GridItemProps;
    uploadGridItem?: GridItemProps;
    uploadDownloadBtnContainer?: GridContainerProps;
    uploadButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    userGridDocumentItem?: GridItemProps;
    userFileUploadGridItem?: GridItemProps;
    userDownloadGridItem?: GridItemProps;
    userDocumentInnerGridItem?: GridItemProps;
    userDocumentUploadBtnGridItem?: GridItemProps;
    msgTextWrapper?: InjectableCss;
    documentUploadMsgWrapper?: InjectableCss;
    nextButtonLink?: InjectableCss;
    managementCoSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
    brandsSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
    buyingGroupSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
    commonTwoColumnGridItem?: GridItemProps;
    userGridContactItem?: GridItemProps;
    userGridAdditionalContactItem?: GridItemProps;
    commonTwoColumnGridContainer?: GridContainerProps;
    classCodeSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
    collectorSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
    priceListSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
    attr5SelectorDynamicDropdown?: DynamicDropdownPresentationProps;
    adminApprovedCheckbox?: CheckboxProps;
    notestxtArea?: TextAreaProps;
    csrNotesGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    internalUserGridItem?: GridItemProps;
    internalUseGridItem?: GridItemProps;
    internalFormFieldGridItem?: GridItemProps;
    internalUseContainer?: GridContainerProps;
    coffeeMakerGridItem?: GridItemProps;
    coffeeMakerDatePicker?: DatePickerPresentationProps;
    userOtherGridItem?: GridItemProps;
    billingCheckbox?: InjectableCss;
    fileNamedHint?: InjectableCss;
    firstWrapper?: InjectableCss;
    mainHeading?: InjectableCss;
    createButtonText?: TypographyProps;
}

export const createAccountStyles: CreateAccountStyles = {
    firstWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    mainHeading: {
        css: css`
            font-size: 28px;
            font-weight: bold;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 20px;
                    `,
                    css`
                        font-size: 20px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    createButtonText: {
        css: css`
            color: ${themeColor.colorSecondary};
            width: 100%;
            font-weight: 700;
            font-size: 14px;
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    mainGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .bottom-spacing {
                                padding-bottom: 32px;
                            }
                        `,
                    ],
                    "minMax",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            .bottom-spacing-other,
                            .bottom-spacing {
                                padding-bottom: 32px;
                            }
                        `,
                    ],
                    "minMax",
                )}
        `,
    },
    submitBtnGridContainer: {
        css: css`
            margin-top: 15px !important;
        `,
    },
    userGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 100px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    userGridBillingItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            > div > div {
                                margin-bottom: 15px !important;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    userGridContactItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    userGridAdditionalContactItem: {
        width: 12,
    },
    commonTwoColumnGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            flex-direction: column;
        `,
    },
    userOthersGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    internalUserGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            &:not(:empty) {
                border-top: 1px solid ${themeColor.colorBorderGrey};
                margin-top: 16px !important;
                padding-top: 24px;
            }
            &:empty {
                padding: 0;
            }
        `,
    },
    internalUseGridItem: {
        width: 12,
    },
    coffeeMakerGridItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            > div,
            > div > div {
                width: 100%;
            }
            padding: 8px 15px;
        `,
    },
    internalFormFieldGridItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            padding: 8px 15px;
        `,
    },
    accountManagerGridContainer: {
        css: css`
            > div {
                > div {
                    padding-top: 5px;
                    padding-bottom: 0;
                }
            }
        `,
    },
    userGridDocumentItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin-top: 24px !important;
                        `,
                    ],
                    "minMax",
                )}
        `,
    },
    userGridShippingItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            .mb-16 {
                margin-bottom: 16px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        null,
                        css`
                            .shipping-address-grid {
                                justify-content: space-between;
                                h5,
                                > div {
                                    display: inline-block;
                                }
                                > div {
                                    margin-top: 4px;
                                }
                            }
                        `,
                    ],
                    "minMax",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            .shipping-address-grid {
                                flex-direction: column;
                                > div {
                                    margin-top: 7px;
                                }
                                h5,
                                > div {
                                    display: inline-block;
                                }
                            }
                        `,
                    ],
                    "max",
                )}

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .shipping-address-grid {
                                padding-bottom: 0;
                            }
                        `,
                    ],
                    "minMax",
                )}
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            .shipping-address-grid {
                                padding-bottom: 0;
                            }
                        `,
                    ],
                    "minMax",
                )}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            > div > div {
                                margin-bottom: 15px !important;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    userRadioBtnGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding: 8px 15px;
            > div {
                display: block;
                > div {
                    display: inline-block;
                    margin-right: 20px;
                }
            }
            fieldset {
                flex-direction: row;
                justify-content: space-between;
                width: 100%;
            }
            legend {
                padding: 0;
                margin-bottom: 10px;
                display: inline-flex;
                float: left;
                width: 75%;
                margin-top: -2px;
            }
            [class*="RadioStyle-"] {
                display: inline-flex;
                padding-left: 10px;
                align-items: flex-start;
                width: 90px;
                margin-top: 0 !important;
            }
            &.error-field {
                > fieldset {
                    position: relative;
                    > span {
                        position: absolute;
                        bottom: 0;
                    }
                    > legend {
                        margin-bottom: 25px !important;
                    }
                }
            }
        `,
    },
    userPurchaseGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            > div {
                                margin-top: 15px !important;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    userGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 400px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    emailGridItem: {
        width: 12,
        css: css`
            padding: 8px 15px;
        `,
    },
    uploadGridItem: {
        width: 12,
        css: css`
            label {
                line-height: 36px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            width: 47%;
                            flex-basis: 47%;
                            padding-right: 0;
                            padding-top: 0;
                            padding-bottom: 0;
                            label {
                                padding: 0 6px;
                            }
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        null,
                        css`
                            width: 40%;
                            flex-basis: 40%;
                        `,
                    ],
                    "minMax",
                )}
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            label {
                                width: 100%;
                                margin-left: 0;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    nextBtnGridItem: {
        width: [12, 8, 6, 5, 4],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-right: 35px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    downloadGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            width: 53%;
                            flex-basis: 53%;
                            align-items: end;
                            padding-top: 0;
                            padding-bottom: 0;
                            button {
                                padding: 0 10px;
                                width: 100%;
                            }
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        null,
                        css`
                            width: 60%;
                            flex-basis: 60%;
                            justify-content: left;
                        `,
                    ],
                    "minMax",
                )}
        `,
    },
    userNameGridItem: {
        width: 12,
        css: css`
            padding: 8px 15px;
        `,
    },
    accountManagerGridItem: {
        width: 12,
        css: css`
            padding: 8px 15px;
        `,
    },
    csrNotesGridItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            padding: 8px 15px;
        `,
    },
    notesGridItem: {
        width: 12,
        css: css`
            padding: 8px 15px;
        `,
    },
    userDocumentInnerGridItem: {
        width: 12,
        css: css`
            padding: 8px 15px 0;
        `,
    },
    userDocumentUploadBtnGridItem: {
        width: 12,
        css: css`
            padding: 8px 15px 15px;
            button {
                width: 100%;
            }
            &.view-btn-section {
                > button {
                    width: 50%;
                    margin-right: 5px;
                }
                > a {
                    width: 50%;
                    margin-left: 5px;
                    margin-top: 0;
                    font-weight: bold;
                    justify-content: center;
                    padding: 0;
                }
            }
        `,
    },
    userFileUploadGridItem: {
        width: 12,
        css: css`
            display: block;
            padding: 8px 15px 16px;
            label {
                padding: 0 12px;
                line-height: 36px;
                margin-top: 34px;
            }
            > div {
                align-items: start;
                width: 100%;
            }

            padding-bottom: 10px;
            [class*="IconWrapper"] + [class*="DescriptionWrapper-"] {
                margin-bottom: 32px;
                display: block;
                position: relative;
            }
            [class*="UploadWrapper-"] {
                position: relative;
                > div {
                    > span {
                        max-height: 22px;
                        width: 100%;
                        overflow: visible;
                        position: absolute;
                        min-width: 280px;
                        margin-bottom: 0 !important;
                        height: 22px !important;
                    }
                }
            }
            [class*="UploadWrapper"] {
                [class*="FormInputWrapper-"] {
                    width: 100%;
                    margin: 0 !important;
                    top: 35px;
                }
                > [class*="ButtonWrapper-"] button {
                    line-height: 35px;
                    margin-top: 35px;
                }
            }
        `,
    },
    userDownloadGridItem: {
        width: 12,
        css: css`
            padding: 8px 15px;
            button {
                padding: 0 12px;
                width: 100%;
            }
        `,
    },
    passwordGridItem: {
        width: 12,
        css: css`
            padding: 8px 15px;
        `,
    },
    confirmPasswordGridItem: {
        width: 12,
        css: css`
            padding: 8px 15px;
        `,
    },
    subscriptionGridItem: {
        width: 12,
        css: css`
            padding: 8px 15px;
        `,
    },
    submitErrorGridItem: {
        width: 12,
        css: css`
            padding: 8px 15px;
        `,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            margin-top: 16px !important;
        `,
    },
    requirementsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 50px;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    passwordRequirementsGridContainer: {
        gap: 8,
    },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    passwordRequirementsGridItem: {
        width: 12,
    },
    requirementsTitle: {
        weight: 600,
        variant: "h5",
        as: "h5",
        css: css`
            margin-bottom: 10px !important;
        `,
    },
    documentUploadTitle: {
        css: css`
            font-size: 15px;
            font-weight: 600;
            &:first-child {
                padding-right: 4px;
            }
        `,
    },
    heading: {
        weight: 600,
        variant: "h5",
        css: css`
            margin: 0 1rem 0 0;
        `,
    },
    form: {
        css: css`
            margin-top: 20px;
            input[type="radio"] + label {
                margin-left: -21px;
            }
            .HideField {
                cursor: not-allowed;
            }
        `,
    },
    userRadioBtn: {
        css: css`
            > span {
                margin-bottom: 0 !important;
            }
        `,
    },
    billingCheckbox: {
        css: css`
            legend {
                margin: 0 !important;
            }
        `,
    },
    uploadDownloadBtnContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            &.uploadDownloadBtnContainer {
                                > div {
                                    flex-direction: row-reverse;
                                    margin: 8px -15px;
                                }
                            }
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &.uploadDownloadBtnContainer {
                                > div {
                                    margin: 0 -15px;
                                    > div {
                                        padding-top: 8px;
                                        padding-bottom: 8px;
                                        > div {
                                            width: 100%;
                                            > div {
                                                width: calc(50% - 7px);
                                            }
                                            > div:last-child {
                                                width: calc(50% - 7px);
                                                margin-left: 15px;
                                                text-align: center;
                                            }
                                        }
                                    }
                                }
                                button {
                                    width: 100%;
                                }
                                &.two-btn-section {
                                    button {
                                        width: 50%;
                                    }
                                }
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    cancelButton: {
        css: css`
            margin-left: 15px;
        `,
    },
    uploadButton: {
        css: css`
            span {
                font-weight: bold;
                font-size: 15px;
            }
        `,
    },
    submitButton: {
        css: css`
            margin-top: 25px;
            width: 100%;
        `,
    },
    nextButton: {
        css: css`
            width: 100%;
        `,
    },
    nextButtonLink: {
        css: css`
            margin-top: 20px;
            min-height: 40px;
            line-height: 1;
            padding: 0 21px;
            background: ${themeColor.colorPrimary};
            border: 2px solid ${themeColor.colorPrimary};
            color: ${themeColor.colorWhite};
            &:hover,
            &:focus {
                background: ${themeColor.colorPrimaryHover};
                border-color: ${themeColor.colorPrimaryHover};
            }
            > span {
                color: ${themeColor.colorWhite} !important;
            }
        `,
    },
    msgTextWrapper: {
        css: css`
            p {
                font-size: 15px;
                margin-bottom: 22px;
            }
        `,
    },
    userOtherGridItem: {
        width: [12, 12, 6, 12, 12],
    },
    documentUploadMsgWrapper: {
        css: css`
            margin-top: 20px;
            font-weight: bold;
            color: ${themeColor.colorTextGreen};
        `,
    },
    fileNamedHint: {
        css: css`
            width: 100%;
        `,
    },
};
const styles = createAccountStyles;
const StyledForm = getStyledWrapper("form");
const options1 = ["Yes", "No"];

type State = {
    contactFirstName: string;
    contactLastName: string;
    apLastName: string;
    managementCo: string;
    brands: string;
    customerRegistrationUrl: string | undefined;
    billToId: string | undefined;
    shipToId: string | undefined;
    customerNumber: string;
    IsCustomerExist: boolean;
    // selectedBillToCountry: CountryModel | undefined;
    // selectedBillToState: StateModel | undefined;
    // selectedShipToCountry: CountryModel | undefined;
    // selectedShipToState: StateModel | undefined;
    billToAddress1: string;
    billToAddress2: string;
    billToCity: string;
    billToPostalcode: string;
    shipToAddress1: string;
    shipToAddress2: string;
    shipToCity: string;
    shipToPostalcode: string;
    phone: string;
    fax: string;
    company: string;
    gamingProperty: string;
    contact1: string;
    contact2: string;
    totalRooms: string;
    buyingGroup: string;
    selectedBillToCountryId: string | undefined;
    selectedBillToStateId: string | undefined;
    selectedShipToStateId: string | undefined;
    selectedShipToCountryId: string | undefined;
    cardCharge: string;
    backupCredit: string;
    reCertOrTaxExempt: string;
    contactName: string;
    contactEmail: string;
    avendraID: string;
    // taxable: string;
    contactPhone: string;
    electronicemail: string;
    templateUrlCredit: string | undefined;
    templateUrlTrade: string;
    templateUrlTax: string;
    formErrors: {
        address: string;
        selectedBillToCountry: string;
        selectedBillToState: string;
        selectedShipToCountry: string;
        selectedShipToState: string;
        billToAddress1: string;
        billToAddress2: string;
        billToCity: string;
        billToPostalcode: string;
        shipToAddress1: string;
        shipToAddress2: string;
        shipToCity: string;
        shipToPostalcode: string;
        contact1: string;
        phone: string;
        fax: string;
        company: string;
        contactName: string;
        contactEmail: string;
        contactPhone: string;
        electronicemail: string;
        creditFileError: string;
        taxFileError: string;
        tradeFileError: string;
        managementCo: string;
        brands: string;
        contactFirstName: string;
        contactLastName: string;
        apLastName: string;
        classCode: string;
        priceList: string;
        programList: string;
        collectorList: string;
        dunningEmailAddress: string;
        salesRepList: string;
        totalRooms: string;
        gamingProperty: string;
        cardCharge: string;
        backupCredit: string;
        reCertOrTaxExempt: string;
        question: string;
        shippingCompanyName: string;
        additionalContactEmail: string;
    };
    formSubmitAttempted: boolean;
    isSubmitting: boolean;
    incorrectCreditFileExtension: boolean;
    incorrectTaxFileExtension: boolean;
    incorrectTradeFileExtension: boolean;
    creditApplicationFile: any;
    taxCertificateFile: any;
    tradeApplicationFile: any;
    question: string;
    showCustomerInformationBlock: boolean;
    referenceNumberMessage: string;
    referenceNumber: string;
    isCreditDocUploaded: boolean;
    isTradeDocUploaded: boolean;
    isTaxDocUploaded: boolean;
    isManagerApproved: boolean;
    buyingGroupAttributeLabel: string;
    isBuyingGrpAttVisible: boolean;
    classCode: string;
    priceList: string;
    collector: string;
    custattr5: string;
    adminApproved: boolean;
    salesManagerApproved: boolean;
    accountManagerApproved: boolean;
    csrNotes: string;
    salesPerson: string;
    customerAttribute: CustomerAttributeModel | undefined;
    salesPersonOptions: OptionObject[];
    buyingGroupOptions: OptionObject[];
    managementCoOptions: OptionObject[];
    brandsOptions: OptionObject[];
    priceListOptions: OptionObject[];
    classCodesOptions: OptionObject[];
    collectorsOptions: OptionObject[];
    programsOptions: OptionObject[];
    dunningEmailAddress: string;
    numberOfServers: string;
    coffeeMakersStartDate: Date | undefined;
    creditSpendingLimit: string;
    isLoading: boolean;
    notes: string;
    shippingCompanyName: string;
    additionalContactTitle: string;
    additionalContactFirstName: string;
    additionalContactLastName: string;
    additionalContactEmail: string;
    creditApplicationFilePath: string;
    tradeAgreementFilePath: string;
    taxCertificateFilePath: string;
    sameAsBilling: boolean;
};

class CreateNewAccountForm extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const isvalidRole =
            (this.props.userRole || "").indexOf("TKCCPAccountManager") !== -1 ||
            (this.props.userRole || "").indexOf("TKCCPSalesManager") !== -1 ||
            (this.props.userRole || "").indexOf("TKCCPAdmin") !== -1;

        this.state = {
            dunningEmailAddress: "",
            numberOfServers: "",
            coffeeMakersStartDate: isvalidRole ? new Date() : undefined,
            creditSpendingLimit: "",
            salesPerson: "Select Sales Person",
            csrNotes: "",
            classCode: "Select Class Code",
            priceList: "Select Price List",
            collector: "",
            custattr5: "",
            adminApproved: false,
            salesManagerApproved: false,
            accountManagerApproved: false,
            isBuyingGrpAttVisible: false,
            buyingGroupAttributeLabel: "",
            contactFirstName: "",
            contactLastName: "",
            apLastName: "",
            customerRegistrationUrl: "",
            billToId: "",
            shipToId: "",
            customerNumber: "",
            IsCustomerExist: false,
            billToAddress1: "",
            billToAddress2: "",
            billToCity: "",
            billToPostalcode: "",
            shipToAddress1: "",
            shipToAddress2: "",
            shipToCity: "",
            shipToPostalcode: "",
            phone: "",
            fax: "",
            company: "",
            gamingProperty: "",
            contact1: "",
            contact2: "",
            totalRooms: "",
            buyingGroup: "Select Buying Group",
            selectedBillToCountryId: undefined,
            selectedBillToStateId: undefined,
            selectedShipToStateId: undefined,
            selectedShipToCountryId: undefined,
            managementCo: "Select Management Company",
            brands: "Select Brand",
            cardCharge: "",
            backupCredit: "",
            reCertOrTaxExempt: "",
            contactName: "",
            contactEmail: "",
            avendraID: "",
            // taxable: "",
            contactPhone: "",
            electronicemail: "",
            // selectedBillToCountry: undefined,
            // selectedBillToState: undefined,
            // selectedShipToCountry: undefined,
            // selectedShipToState: undefined,
            templateUrlCredit: this.props.creditApplicationDocumentPath,
            templateUrlTrade: "/UserFiles/documents/tradeagreement.docx",
            templateUrlTax: "/UserFiles/documents/taxcertificate.docx",
            formErrors: {
                address: "",
                selectedBillToCountry: "",
                selectedBillToState: "",
                selectedShipToCountry: "",
                selectedShipToState: "",
                billToAddress1: "",
                billToAddress2: "",
                billToCity: "",
                billToPostalcode: "",
                shipToAddress1: "",
                shipToAddress2: "",
                shipToCity: "",
                shipToPostalcode: "",
                contact1: "",
                phone: "",
                fax: "",
                company: "",
                contactName: "",
                contactEmail: "",
                contactPhone: "",
                electronicemail: "",
                creditFileError: "",
                taxFileError: "",
                tradeFileError: "",
                managementCo: "",
                brands: "",
                contactFirstName: "",
                contactLastName: "",
                apLastName: "",
                classCode: "",
                priceList: "",
                dunningEmailAddress: "",
                programList: "",
                collectorList: "",
                salesRepList: "",
                totalRooms: "",
                gamingProperty: "",
                cardCharge: "",
                backupCredit: "",
                reCertOrTaxExempt: "",
                question: "",
                shippingCompanyName: "",
                additionalContactEmail: "",
            },
            formSubmitAttempted: false,
            isSubmitting: false,
            incorrectCreditFileExtension: false,
            incorrectTaxFileExtension: false,
            incorrectTradeFileExtension: false,
            creditApplicationFile: null,
            taxCertificateFile: null,
            tradeApplicationFile: null,
            question: "",
            showCustomerInformationBlock: true,
            referenceNumberMessage: "",
            referenceNumber: "",
            isCreditDocUploaded: false,
            isTaxDocUploaded: false,
            isTradeDocUploaded: false,
            isManagerApproved: false,
            customerAttribute: {} as CustomerAttributeModel,
            salesPersonOptions: [],
            buyingGroupOptions: [],
            managementCoOptions: [],
            brandsOptions: [],
            priceListOptions: [],
            classCodesOptions: [],
            collectorsOptions: [],
            programsOptions: [],
            isLoading: false,
            notes: "",
            shippingCompanyName: "",
            additionalContactTitle: "",
            additionalContactFirstName: "",
            additionalContactLastName: "",
            additionalContactEmail: "",
            creditApplicationFilePath: "",
            tradeAgreementFilePath: "",
            taxCertificateFilePath: "",
            sameAsBilling: false,
        };
        this.setCustomerAttribute();
    }
    setCustomerAttribute = () => {
        this.props.loadCustomerAttribute({
            onSuccess: response => {
                if (response !== null && response !== undefined) {
                    if (response.salesPerson) {
                        const spOptions: OptionObject[] = response?.salesPerson?.map(value => ({
                            optionText: value,
                            optionValue: value,
                        }));
                        this.setState({ salesPersonOptions: spOptions });
                    }
                    if (response.buyingGroup) {
                        const bgOptions: OptionObject[] = response?.buyingGroup?.map(value => ({
                            optionText: value,
                            optionValue: value,
                        }));
                        this.setState({ buyingGroupOptions: bgOptions });
                    }
                    if (response.managementCo) {
                        const mCoOptions: OptionObject[] = response?.managementCo?.map(value => ({
                            optionText: value,
                            optionValue: value,
                        }));
                        this.setState({ managementCoOptions: mCoOptions });
                    }
                    if (response.brands) {
                        const bOptions: OptionObject[] = response?.brands?.map(value => ({
                            optionText: value,
                            optionValue: value,
                        }));
                        this.setState({ brandsOptions: bOptions });
                    }
                    if (response.priceList) {
                        const plOptions: OptionObject[] = response?.priceList?.map(value => ({
                            optionText: value,
                            optionValue: value,
                        }));
                        this.setState({ priceListOptions: plOptions });
                    }
                    if (response.classCodes) {
                        const ccOptions: OptionObject[] = response?.classCodes?.map(value => ({
                            optionText: value,
                            optionValue: value,
                        }));
                        this.setState({ classCodesOptions: ccOptions });
                    }
                    if (response.collectors) {
                        const cOptions: OptionObject[] = response?.collectors?.map(value => ({
                            optionText: value,
                            optionValue: value,
                        }));
                        this.setState({ collectorsOptions: cOptions });
                    }
                    if (response.programs) {
                        const pOptions: OptionObject[] = response?.programs?.map(value => ({
                            optionText: value,
                            optionValue: value,
                        }));
                        this.setState({ programsOptions: pOptions });
                    }
                }
            },
        });
    };

    displaySave = () => {
        let displaySaveButton = true;

        const displayAsperRole =
            (this.props.userRole || "").indexOf("TKCCPAccountManager") !== -1 ||
            (this.props.userRole || "").indexOf("TKCCPSalesManager") !== -1 ||
            (this.props.userRole || "").indexOf("TKCCPAdmin") !== -1;

        if (
            displayAsperRole &&
            (this.state.accountManagerApproved || this.state.salesManagerApproved || this.state.adminApproved)
        ) {
            displaySaveButton = true;
        } else if (this.state.accountManagerApproved || this.state.salesManagerApproved || this.state.adminApproved) {
            displaySaveButton = false;
        }
        return displaySaveButton;
    };

    async UNSAFE_componentWillMount() {
        if (!this.props.countriesState.value && !this.props.countriesState.isLoading) {
            this.props.loadCountries(getCountriesApiParameter);
        }
        const refNumber = this.props.referenceNumber;
        if (refNumber) {
            this.setState({
                IsCustomerExist: true,
            });
            const data = refNumber;
            const referenceNumber = atob(data);
            const result = await this.props.loadCustomerDetails({
                referenceNumber,
                onSuccess: response => {
                    if (
                        response !== null &&
                        response !== undefined &&
                        response.referenceNumberExist !== null &&
                        response.status === 200
                    ) {
                        if (response.referenceNumberExist) {
                            if (response.billToModel !== null && response.shipToModel !== null) {
                                // const getCountry = this.props.countriesState.value;
                                // const getSelectedBillToCountry = getCountry?.find(o => o.id === response.billToModel?.country?.id);
                                // const getSelectedBillToState = getSelectedBillToCountry ? getSelectedBillToCountry?.states?.find(o => o.id === response.billToModel?.state?.id) : undefined;
                                // const getSelectedShipToCountry = getCountry?.find(o => o.id === response.shipToModel?.country?.id);
                                // const getSelectedShipToState = getSelectedShipToCountry ? getSelectedShipToCountry?.states?.find(o => o.id === response.shipToModel?.state?.id) : undefined;

                                const splitQus = response.billToModel?.properties["security Question"].split("/");

                                if (
                                    response.billToModel?.properties["credit Application"] !== undefined &&
                                    response.billToModel?.properties["credit Application"] !== "" &&
                                    response.billToModel?.properties["trade Agreement"] !== undefined &&
                                    response.billToModel?.properties["trade Agreement"] !== "" &&
                                    response.billToModel?.properties["tax Certificate"] !== undefined &&
                                    response.billToModel?.properties["tax Certificate"] !== ""
                                ) {
                                    this.setState({
                                        isCreditDocUploaded: true,
                                        isTaxDocUploaded: true,
                                        isTradeDocUploaded: true,
                                    });
                                }

                                if (
                                    response.billToModel?.properties["accountManager_Approve"] === "true" ||
                                    response.billToModel?.properties["salesManager_Approve"] === "true" ||
                                    response.billToModel?.properties["admin_Approval"] === "true"
                                ) {
                                    this.setState({
                                        isManagerApproved: true,
                                    });
                                }

                                const buyingGrpAttVal = this.setBuyingGroupAttributeLabel(
                                    response.billToModel?.properties["buyingGroup"],
                                );
                                if (buyingGrpAttVal !== undefined && buyingGrpAttVal !== "Buying Group Attribute") {
                                    this.setState({
                                        buyingGroupAttributeLabel: buyingGrpAttVal,
                                        isBuyingGrpAttVisible: true,
                                    });
                                } else {
                                    this.setState({ isBuyingGrpAttVisible: false });
                                }

                                this.setState({
                                    IsCustomerExist: true,
                                    referenceNumber,
                                    customerNumber: response.billToModel.customerNumber,
                                    // Load BillTO Data /,
                                    billToId: response.billToModel?.id,
                                    billToAddress1: response.billToModel?.address1,
                                    billToAddress2: response.billToModel?.address2,
                                    // selectedBillToState: getSelectedBillToState,
                                    // selectedBillToCountry: getSelectedBillToCountry,
                                    billToCity: response.billToModel?.city,
                                    billToPostalcode: response.billToModel?.postalCode,
                                    phone: response.billToModel?.phone,
                                    fax: response.billToModel?.fax,
                                    contactEmail: response.billToModel?.email,
                                    company: response.billToModel?.companyName,
                                    contact1: response.billToModel?.firstName,

                                    // Load ShipTo Data //
                                    shipToId: response.shipToModel?.id,
                                    shippingCompanyName: response.shipToModel?.companyName,
                                    shipToAddress1: response.shipToModel?.address1,
                                    shipToAddress2: response.shipToModel?.address2,
                                    // selectedShipToState: getSelectedShipToState,
                                    // selectedShipToCountry: getSelectedShipToCountry,
                                    shipToCity: response.shipToModel?.city,
                                    shipToPostalcode: response.shipToModel?.postalCode,
                                    contact2: response.shipToModel?.firstName,

                                    // Load country and state

                                    selectedBillToCountryId: response.billToModel?.country?.id,
                                    selectedBillToStateId: response.billToModel?.state?.id,
                                    selectedShipToStateId: response.shipToModel?.state?.id,
                                    selectedShipToCountryId: response.shipToModel?.country?.id,

                                    // Load Properties
                                    contactName: response.billToModel?.properties["apContactName"],
                                    contactPhone: response.billToModel?.properties["apContactNumber"],
                                    buyingGroup: response.billToModel?.properties["buyingGroup"],
                                    gamingProperty: response.billToModel?.properties["gamingProperty"],
                                    totalRooms: response.billToModel?.properties["totalRooms"],
                                    cardCharge: response.billToModel?.properties["isChargeCard"],
                                    backupCredit: response.billToModel?.properties["isBackupCreditCard"],
                                    reCertOrTaxExempt: response.billToModel?.properties["isRecertOrTaxExempt"],
                                    avendraID: response.billToModel?.properties["avendraID"],
                                    // taxable: response.billToModel?.properties["taxable"],
                                    electronicemail: response.billToModel?.properties["electronicInvoicingEmail"],
                                    question: splitQus && splitQus[1] && splitQus[1].length > 0 ? splitQus[1] : "Yes",
                                    managementCo: response.billToModel?.properties["management_Co"],
                                    brands: response.billToModel?.properties["brands"],
                                    contactFirstName: response.billToModel?.properties["contactFirstName"],
                                    contactLastName: response.billToModel?.properties["contactLastName"],
                                    apLastName: response.billToModel?.properties["apContactLastName"],
                                    accountManagerApproved:
                                        response.billToModel?.properties["accountManager_Approve"] === "true",
                                    salesManagerApproved:
                                        response.billToModel?.properties["salesManager_Approve"] === "true",
                                    adminApproved: response.billToModel?.properties["admin_Approval"] === "true",
                                    custattr5: response.billToModel?.properties["cusT_ATTR5"]
                                        ? response.billToModel?.properties["cusT_ATTR5"]
                                        : "Select Program",
                                    collector: response.billToModel?.properties["collectors"]
                                        ? response.billToModel?.properties["collectors"]
                                        : "Select Collector",
                                    classCode: response.billToModel?.properties["classCodes"]
                                        ? response.billToModel?.properties["classCodes"]
                                        : "Select Class Code",
                                    priceList: response.billToModel?.properties["priceLists"]
                                        ? response.billToModel?.properties["priceLists"]
                                        : "Select Price List",
                                    csrNotes: response.billToModel?.properties["csrNotes"],
                                    notes: response.billToModel?.properties["notes"],
                                    salesPerson: response.properties["salesPerson"]
                                        ? response.properties["salesPerson"]
                                        : "Select Sales Person",

                                    dunningEmailAddress: response.billToModel?.properties["dunningEmailAddress"],
                                    numberOfServers: response.billToModel?.properties["numberofServers"],
                                    creditSpendingLimit: response.billToModel?.properties["creditSpendingLimit"],
                                    coffeeMakersStartDate: response.billToModel?.properties["coffeeMakerStartDate"]
                                        ? new Date(response.billToModel?.properties["coffeeMakerStartDate"])
                                        : undefined,
                                    additionalContactTitle: response.billToModel?.properties["additionalContactTitle"],
                                    additionalContactFirstName:
                                        response.billToModel?.properties["additionalContactFirstName"],
                                    additionalContactLastName:
                                        response.billToModel?.properties["additionalContactLastName"],
                                    additionalContactEmail: response.billToModel?.properties["additionalContactEmail"],
                                    creditApplicationFilePath: this.GetDocumentfilePath(
                                        response.billToModel?.properties["customerReferenceNumber"],
                                        response.billToModel?.properties["credit Application"],
                                    ),
                                    tradeAgreementFilePath: this.GetDocumentfilePath(
                                        response.billToModel?.properties["customerReferenceNumber"],
                                        response.billToModel?.properties["trade Agreement"],
                                    ),
                                    taxCertificateFilePath: this.GetDocumentfilePath(
                                        response.billToModel?.properties["customerReferenceNumber"],
                                        response.billToModel?.properties["tax Certificate"],
                                    ),
                                    sameAsBilling: response.billToModel?.properties["sameAsBilling"] === "true",
                                });
                            }
                        }
                    }
                },
            });
        }
    }

    GetDocumentfilePath = (customerReferenceNumber: string, filePath: string) => {
        if (customerReferenceNumber && customerReferenceNumber.length > 0 && filePath && filePath.length > 0) {
            const fileName = this.GetDocumentFileName(filePath);
            return `${customerReferenceNumber}/${fileName}`;
        }
        return "";
    };

    GetDocumentFileName = (path: string) => {
        if (path && path.length > 0) {
            return path.replace(/^.*[\\\/]/, "");
        }
        return "";
    };
    setBuyingGroupAttributeLabel = (value: string) => {
        if (value && value.length > 0) {
            switch (value) {
                case "BUYING GROUP-AVENDRA":
                    return "Avendra Acct#";
                case "BUYING GROUP-HSM":
                    return "Inn Code";
                case "BUYING GROUP-FOODBUY":
                    return "Foodbuy";
                case "BUYING GROUP-INTERSTATE":
                    return "Interstate P-Code";
                default:
                    return "Buying Group Attribute";
            }
        }
    };
    setFocus = (value: string | undefined | null) => {
        if (value && value.length > 0) {
            const elementToFocus = document.getElementById(value);
            if (elementToFocus) {
                elementToFocus.focus();
            }
        }
    };
    refrenceNumberHandler = (message: string, refNumber: string) => {
        if (refNumber == null || refNumber === "") {
            return;
        }

        this.setState({
            isManagerApproved:
                this.state.accountManagerApproved || this.state.salesManagerApproved || this.state.adminApproved,
        });

        const data = refNumber;
        const buff = btoa(data);
        const queryString = `referencenumber=${buff}`;
        this.setState({
            IsCustomerExist: true,
            showCustomerInformationBlock: false,
            referenceNumberMessage: message,
            referenceNumber: refNumber,
            customerRegistrationUrl: `${window.location.origin.toString()}/CustomerRegistration?${queryString}`,
        });
    };

    billToAddress1Handler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const formErrors = this.state.formErrors;
        if (this.state.sameAsBilling) {
            if (value.length > 0) {
                formErrors.billToAddress1 = "";
                formErrors.shipToAddress1 = "";
                this.setState({ formErrors });
            }
            this.setState({
                billToAddress1: event.target.value,
                shipToAddress1: event.target.value,
            });
        } else {
            if (value.length > 0) {
                formErrors.billToAddress1 = "";
                this.setState({ formErrors });
            }
            this.setState({
                billToAddress1: event.target.value,
            });
        }
    };
    billToAddress2Handler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        const billToAddress2 = this.state.formErrors.billToAddress2;

        if (event.target.value.length > 0) {
            formErrors.billToAddress2 = "";
            this.setState({ formErrors });
        }
        this.setState({
            billToAddress2: event.target.value,
        });
    };
    billToCityHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (this.state.sameAsBilling) {
            if (event.target.value.length > 0) {
                formErrors.billToCity = "";
                formErrors.shipToCity = "";
                this.setState({ formErrors });
            }
            this.setState({
                billToCity: event.target.value,
                shipToCity: event.target.value,
            });
        } else {
            if (event.target.value.length > 0) {
                formErrors.billToCity = "";
                this.setState({ formErrors });
            }
            this.setState({
                billToCity: event.target.value,
            });
        }
    };

    billToPostalCodeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (this.state.sameAsBilling) {
            if (event.target.value.length > 0) {
                formErrors.billToPostalcode = "";
                formErrors.shipToPostalcode = "";
                this.setState({ formErrors });
            }
            this.setState({
                billToPostalcode: event.target.value,
                shipToPostalcode: event.target.value,
            });
        } else {
            if (event.target.value.length > 0) {
                formErrors.billToPostalcode = "";
                this.setState({ formErrors });
            }
            this.setState({
                billToPostalcode: event.target.value,
            });
        }
    };
    shipToAddress1Handler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.shipToAddress1 = "";
            this.setState({ formErrors });
        }
        this.setState({
            shipToAddress1: event.target.value,
        });
    };
    shipToAddress2Handler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.shipToAddress2 = "";
            this.setState({ formErrors });
        }
        this.setState({
            shipToAddress2: event.target.value,
        });
    };
    shipToCityHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.shipToCity = "";
            this.setState({ formErrors });
        }
        this.setState({
            shipToCity: event.target.value,
        });
    };
    shipToPostalCodeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.shipToPostalcode = "";
            this.setState({ formErrors });
        }
        this.setState({
            shipToPostalcode: event.target.value,
        });
    };
    faxChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.fax = "";
            this.setState({ formErrors });
        }
        this.setState({
            fax: event.target.value,
        });
    };
    contactNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.contactName = "";
            this.setState({ formErrors });
        }
        this.setState({
            contactName: event.target.value,
        });
    };

    companyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (this.state.sameAsBilling) {
            if (event.target.value.length > 0) {
                formErrors.company = "";
                formErrors.shippingCompanyName = "";
                this.setState({ formErrors });
            }
            this.setState({
                company: event.target.value,
                shippingCompanyName: event.target.value,
            });
        } else {
            if (event.target.value.length > 0) {
                formErrors.company = "";
                this.setState({ formErrors });
            }
            this.setState({
                company: event.target.value,
            });
        }
    };
    shippingCompanyNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.shippingCompanyName = "";
            this.setState({ formErrors });
        }
        this.setState({
            shippingCompanyName: event.target.value,
        });
    };
    emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.contactEmail = "";
            this.setState({ formErrors });
        }
        this.setState({
            contactEmail: event.target.value,
        });
    };

    csrNotesChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            csrNotes: event.target.value,
        });
    };
    notesChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            notes: event.target.value,
        });
    };
    additionalContactTitleChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            additionalContactTitle: event.target.value,
        });
    };
    additionalContactFirstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            additionalContactFirstName: event.target.value,
        });
    };
    additionalContactLastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            additionalContactLastName: event.target.value,
        });
    };
    additionalContactEmailHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (/\w+([-+."]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/g.test(this.state.additionalContactEmail)) {
            formErrors.additionalContactEmail = "";
            this.setState({ formErrors });
        }
        this.setState({
            additionalContactEmail: event.target.value,
        });
    };

    options1 = ["Yes", "No"];

    accManagerApprovedPropertyHandler: CheckboxProps["onChange"] = (_, value) => {
        this.setState({
            accountManagerApproved: value,
        });
    };
    sameAsBillingFlagHandler: CheckboxProps["onChange"] = (_, value) => {
        if (value) {
            this.setState({
                sameAsBilling: value,
                shippingCompanyName: this.state.company,
                shipToAddress1: this.state.billToAddress1,
                // shipToAddress2: this.state.billToAddress2,
                selectedShipToCountryId: this.state.selectedBillToCountryId,
                selectedShipToStateId: this.state.selectedBillToStateId,
                shipToCity: this.state.billToCity,
                shipToPostalcode: this.state.billToPostalcode,
            });
        } else {
            this.setState({
                sameAsBilling: value,
                shippingCompanyName: "",
                shipToAddress1: "",
                // shipToAddress2: "",
                selectedShipToCountryId: undefined,
                selectedShipToStateId: undefined,
                shipToCity: "",
                shipToPostalcode: "",
            });
        }
    };
    salesManagerApprovedPropertyHandler: CheckboxProps["onChange"] = (_, value) => {
        this.setState({
            salesManagerApproved: value,
        });
    };

    adminApprovedPropertyHandler: CheckboxProps["onChange"] = (_, value) => {
        this.setState({
            adminApproved: value,
        });
    };

    cardChargeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.cardCharge = "";
            this.setState({ formErrors });
        }

        this.setState({
            cardCharge: event.target.value,
        });
    };
    bachupCreditCardHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.backupCredit = "";
            this.setState({ formErrors });
        }
        this.setState({
            backupCredit: event.target.value,
        });
    };
    gamingPropertyHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.gamingProperty = "";
            this.setState({ formErrors });
        }
        this.setState({
            gamingProperty: event.target.value,
        });
    };

    reCertTaxExceptHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.reCertOrTaxExempt = "";
            this.setState({ formErrors });
        }
        this.setState({
            reCertOrTaxExempt: event.target.value,
        });
    };
    avendraIDChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            avendraID: event.target.value,
        });
    };
    totalRoomsChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const formErrors = this.state.formErrors;
        if (value.length > 0) {
            formErrors.totalRooms = "";
            this.setState({ formErrors });
        }

        this.setState({
            totalRooms: event.target.value,
        });
    };
    // taxableHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     this.setState({
    //         taxable: event.target.value,
    //     });
    // };
    contactPhoneHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.contactPhone = "";
            this.setState({ formErrors });
        }
        this.setState({
            //    contactPhone: event.target.value,
            contactPhone: formatPhoneNumber(event.target.value),
        });
    };
    electronicEmailHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.electronicemail = "";
            this.setState({ formErrors });
        }
        this.setState({
            electronicemail: event.target.value,
        });
    };

    phoneHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.phone = "";
            this.setState({ formErrors });
        }
        this.setState({
            phone: formatPhoneNumber(event.target.value),
        });
    };
    contact1Handler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.contact1 = "";
            this.setState({ formErrors });
        }
        this.setState({
            contact1: event.target.value,
        });
    };

    contact2Handler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            contact2: event.target.value,
        });
    };

    buyingGroupChangeHandler = (value?: string) => {
        if (!value) {
            return null;
        }
        const buyingGrpAttVal = this.setBuyingGroupAttributeLabel(value);
        if (buyingGrpAttVal !== undefined && buyingGrpAttVal !== "Buying Group Attribute") {
            this.setState({ buyingGroupAttributeLabel: buyingGrpAttVal, isBuyingGrpAttVisible: true, avendraID: "" });
        } else {
            this.setState({ isBuyingGrpAttVisible: false });
        }
        this.setState({
            buyingGroup: value,
        });
    };

    classCodeChangeHandler = (value?: string) => {
        const formErrors = this.state.formErrors;
        if (value && value.length > 0 && !value?.toLowerCase().includes("select")) {
            formErrors.classCode = "";
            this.setState({ formErrors });
        }
        if (!value) {
            formErrors.classCode = "";
            this.setState({ formErrors });
            return null;
        }

        this.setState({
            classCode: value,
        });
    };
    priceListChangeHandler = (value?: string) => {
        const formErrors = this.state.formErrors;
        if (value && value.length > 0 && !value?.toLowerCase().includes("select")) {
            formErrors.priceList = "";
            this.setState({ formErrors });
        }
        if (!value) {
            formErrors.priceList = "";
            this.setState({ formErrors });
            return null;
        }

        this.setState({
            priceList: value,
        });
    };
    attribute5ChangeHandler = (value?: string) => {
        const formErrors = this.state.formErrors;
        if (value && value.length > 0 && !value?.toLowerCase().includes("select")) {
            formErrors.programList = "";
            this.setState({ formErrors });
        }
        if (!value) {
            formErrors.programList = "";
            this.setState({ formErrors });
            return null;
        }

        this.setState({
            custattr5: value,
        });
    };
    collectorChangeHandler = (value?: string) => {
        const formErrors = this.state.formErrors;
        if (value && value.length > 0 && !value?.toLowerCase().includes("select")) {
            formErrors.collectorList = "";
            this.setState({ formErrors });
        }
        if (!value) {
            formErrors.collectorList = "";
            this.setState({ formErrors });
            return null;
        }

        this.setState({
            collector: value,
        });
    };

    salesPersonChangeHandler = (value?: string) => {
        const formErrors = this.state.formErrors;
        if (value && value.length > 0 && !value?.toLowerCase().includes("select")) {
            formErrors.salesRepList = "";
            this.setState({ formErrors });
        }
        if (!value) {
            formErrors.salesRepList = "";
            this.setState({ formErrors });
            return null;
        }

        this.setState({
            salesPerson: value,
        });
    };

    managementCoChangeHandler = (value?: string) => {
        if (!value) {
            return null;
        }
        this.setState({
            managementCo: value,
        });
    };

    contactFirstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.contactFirstName = "";
            this.setState({ formErrors });
        }
        this.setState({
            contactFirstName: event.target.value,
        });
    };
    contactLastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.contactLastName = "";
            this.setState({ formErrors });
        }
        this.setState({
            contactLastName: event.target.value,
        });
    };
    apContactLastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const formErrors = this.state.formErrors;
        if (value.length > 0) {
            formErrors.apLastName = "";
            this.setState({ formErrors });
        }

        this.setState({
            apLastName: event.target.value,
        });
    };

    brandsChangeHandler = (value?: string) => {
        if (!value) {
            return null;
        }
        this.setState({
            brands: value,
        });
    };

    handleSelectCountry = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const formErrors = this.state.formErrors;
        if (this.state.sameAsBilling) {
            if (event.target.value.length > 0) {
                formErrors.selectedBillToCountry = "";
                formErrors.selectedShipToCountry = "";
                this.setState({ formErrors });
            }
            const selectedCountry = this.props.countriesState.value?.find(o => o.id === event.target.value);
            if (!selectedCountry) {
                return;
            }

            this.setState({
                // selectedBillToCountry,
                // selectedBillToState: undefined,
                // selectedShipToCountry:selectedBillToCountry,
                // selectedShipToState:undefined,
                selectedBillToCountryId: selectedCountry.id,
                selectedBillToStateId: undefined,
                selectedShipToStateId: undefined,
                selectedShipToCountryId: selectedCountry.id,
            });
        } else {
            if (event.target.value.length > 0) {
                formErrors.selectedBillToCountry = "";
                this.setState({ formErrors });
            }
            const selectedCountry = this.props.countriesState.value?.find(o => o.id === event.target.value);
            if (!selectedCountry) {
                return;
            }

            this.setState({
                // selectedBillToCountry,
                // selectedBillToState: undefined,
                selectedBillToCountryId: selectedCountry.id,
                selectedBillToStateId: undefined,
            });
        }
    };

    handleSelectState = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const {
            countriesState: { value: countries },
        } = this.props;

        const formErrors = this.state.formErrors;
        if (this.state.sameAsBilling) {
            if (event.target.value.length > 0) {
                formErrors.selectedBillToState = "";
                formErrors.selectedShipToState = "";
                this.setState({ formErrors });
            }
            const { selectedBillToCountryId } = this.state;
            if (!selectedBillToCountryId) {
                return;
            }
            const getCountry = countries;
            const getSelectedBillToCountry = getCountry?.find(o => o.id === selectedBillToCountryId);

            const selectedStateId = getSelectedBillToCountry
                ? getSelectedBillToCountry?.states?.find(o => o.id === event.target.value)
                : undefined;
            if (!selectedStateId) {
                return;
            }

            this.setState({
                selectedBillToStateId: selectedStateId.id,
                selectedShipToStateId: selectedStateId.id,
            });
        } else {
            if (event.target.value.length > 0) {
                formErrors.selectedBillToState = "";
                this.setState({ formErrors });
            }
            const { selectedBillToCountryId } = this.state;
            if (!selectedBillToCountryId) {
                return;
            }
            const getCountry = countries;
            const getSelectedBillToCountry = getCountry?.find(o => o.id === selectedBillToCountryId);
            const selectedStateId = getSelectedBillToCountry
                ? getSelectedBillToCountry?.states?.find(o => o.id === event.target.value)
                : undefined;
            if (!selectedStateId) {
                return;
            }

            this.setState({
                selectedBillToStateId: selectedStateId.id,
            });
        }
    };
    handleSelectShipToCountry = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.selectedShipToCountry = "";
            this.setState({ formErrors });
        }
        const selectedCountry = this.props.countriesState.value?.find(o => o.id === event.target.value);
        if (!selectedCountry) {
            return;
        }

        this.setState({
            // selectedShipToCountry,
            // selectedShipToState: undefined,
            selectedShipToCountryId: selectedCountry.id,
            selectedShipToStateId: undefined,
        });
    };

    handleSelectShipToState = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const {
            countriesState: { value: countries },
        } = this.props;

        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.selectedShipToState = "";
            this.setState({ formErrors });
        }
        const { selectedShipToCountryId } = this.state;
        if (!selectedShipToCountryId) {
            return;
        }
        const getCountry = countries;
        const getSelectedShipToCountry = getCountry?.find(o => o.id === selectedShipToCountryId);

        const selectedStateId = getSelectedShipToCountry
            ? getSelectedShipToCountry?.states?.find(o => o.id === event.target.value)
            : undefined;
        if (!selectedStateId) {
            return;
        }

        this.setState({
            // selectedShipToState,
            selectedShipToStateId: selectedStateId.id,
        });
    };
    creditfileHandler = (event: React.ChangeEvent<any>) => {
        const formErrors = this.state.formErrors;

        const uploadedFile = event.target.files[0];
        this.setState({ creditApplicationFile: uploadedFile });

        if (event.target.files[0].name.length > 0) {
            formErrors.creditFileError = "";
            this.setState({ formErrors });
        }
        if (uploadedFile) {
            const fileExtension = getFileExtension(uploadedFile.name);
            const incorrectCreditFileExtension = ["docx", "pdf", "jpeg"].indexOf(fileExtension) === -1;
            this.setState({
                incorrectCreditFileExtension,
            });
        }
    };
    taxfileHandler = (event: React.ChangeEvent<any>) => {
        const formErrors = this.state.formErrors;
        const uploadedFile = event.target.files[0];

        if (event.target.files[0].name.length > 0) {
            formErrors.taxFileError = "";
            this.setState({ formErrors });
        }
        if (uploadedFile) {
            const fileExtension = getFileExtension(uploadedFile.name);
            const incorrectTaxFileExtension = ["docx", "pdf", "jpeg"].indexOf(fileExtension) === -1;
            this.setState({
                incorrectTaxFileExtension,
            });
        }
        this.setState({ taxCertificateFile: uploadedFile });
    };
    tradefileHandler = (event: React.ChangeEvent<any>) => {
        const formErrors = this.state.formErrors;

        const uploadedFile = event.target.files[0];
        this.setState({ tradeApplicationFile: uploadedFile });

        if (event.target.files[0].name.length > 0) {
            formErrors.tradeFileError = "";
            this.setState({ formErrors });
        }
        if (uploadedFile) {
            const fileExtension = getFileExtension(uploadedFile.name);
            const incorrectTradeFileExtension = ["docx", "pdf", "jpeg"].indexOf(fileExtension) === -1;
            this.setState({
                incorrectTradeFileExtension,
            });
        }
    };
    securityQuestionHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.question = "";
            this.setState({ formErrors });
        }
        this.setState({
            question: event.target.value,
        });
    };
    downloadCreditTemplateHandler = () => {
        window.open(`${window.location.origin}${this.state.templateUrlCredit}`);
        this.state.customerRegistrationUrl;
    };

    dunningEmailAddHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formErrors = this.state.formErrors;
        if (event.target.value.length > 0) {
            formErrors.dunningEmailAddress = "";
            this.setState({ formErrors });
        }
        this.setState({
            dunningEmailAddress: event.target.value,
        });
    };

    creditSpendingLimitHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            creditSpendingLimit: event.target.value,
        });
    };
    numberofServerHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            numberOfServers: event.target.value,
        });
    };

    coffeemakerHandler = ({ selectedDay }: DatePickerState) => {
        this.setState({
            coffeeMakersStartDate: selectedDay || new Date(),
        });
    };
    resetForm = () => {
        const state = this.state;
        this.setState({
            contactFirstName: "",
            contactLastName: "",
            apLastName: "",
            billToId: "",
            shipToId: "",
            customerNumber: "",
            IsCustomerExist: false,
            billToAddress1: "",
            billToAddress2: "",
            billToCity: "",
            billToPostalcode: "",
            shipToAddress1: "",
            shipToAddress2: "",
            shipToCity: "",
            shipToPostalcode: "",
            phone: "",
            fax: "",
            company: "",
            gamingProperty: "",
            contact1: "",
            contact2: "",
            totalRooms: "",
            buyingGroup: "Select Buying Group",
            selectedBillToCountryId: undefined,
            selectedBillToStateId: undefined,
            selectedShipToStateId: undefined,
            selectedShipToCountryId: undefined,
            managementCo: "Select Management Company",
            brands: "Select Brand",
            cardCharge: "",
            backupCredit: "",
            reCertOrTaxExempt: "",
            contactName: "",
            contactEmail: "",
            avendraID: "",
            // taxable: "",
            contactPhone: "",
            electronicemail: "",
            // selectedBillToCountry: undefined,
            // selectedBillToState: undefined,
            // selectedShipToCountry: undefined,
            // selectedShipToState: undefined,
            templateUrlCredit: this.props.creditApplicationDocumentPath,
            templateUrlTrade: "/UserFiles/documents/tradeagreement.docx",
            templateUrlTax: "/UserFiles/documents/taxcertificate.docx",
            formErrors: {
                address: "",
                selectedBillToCountry: "",
                selectedBillToState: "",
                selectedShipToCountry: "",
                selectedShipToState: "",
                billToAddress1: "",
                billToAddress2: "",
                billToCity: "",
                billToPostalcode: "",
                shipToAddress1: "",
                shipToAddress2: "",
                shipToCity: "",
                shipToPostalcode: "",
                contact1: "",
                phone: "",
                fax: "",
                company: "",
                contactName: "",
                contactEmail: "",
                contactPhone: "",
                electronicemail: "",
                creditFileError: "",
                taxFileError: "",
                tradeFileError: "",
                managementCo: "",
                brands: "",
                contactFirstName: "",
                contactLastName: "",
                apLastName: "",
                classCode: "",
                priceList: "",
                dunningEmailAddress: "",
                programList: "",
                collectorList: "",
                salesRepList: "",
                totalRooms: "",
                gamingProperty: "",
                cardCharge: "",
                backupCredit: "",
                reCertOrTaxExempt: "",
                question: "",
                shippingCompanyName: "",
                additionalContactEmail: "",
            },
            formSubmitAttempted: false,
            isSubmitting: false,
            incorrectCreditFileExtension: false,
            incorrectTaxFileExtension: false,
            incorrectTradeFileExtension: false,
            creditApplicationFile: null,
            taxCertificateFile: null,
            tradeApplicationFile: null,
            question: "",
            showCustomerInformationBlock: true,
            referenceNumberMessage: "",
            referenceNumber: "",
            isCreditDocUploaded: false,
            isTradeDocUploaded: false,
            isTaxDocUploaded: false,
            isManagerApproved: false,
            buyingGroupAttributeLabel: "",
            isBuyingGrpAttVisible: false,
            accountManagerApproved: false,
            salesManagerApproved: false,
            adminApproved: false,
            classCode: "Select Class Code",
            collector: "Select Collector",
            priceList: "Select Price List",
            custattr5: "Select Program",
            salesPerson: "Select Sales Person",
            csrNotes: "",
            coffeeMakersStartDate: new Date(),
            numberOfServers: "",
            creditSpendingLimit: "",
            dunningEmailAddress: "",
            isLoading: false,
            notes: "",
            shippingCompanyName: "",
            additionalContactTitle: "",
            additionalContactFirstName: "",
            additionalContactLastName: "",
            additionalContactEmail: "",
            creditApplicationFilePath: "",
            tradeAgreementFilePath: "",
            taxCertificateFilePath: "",
            sameAsBilling: false,
        });
    };

    validateForm = (isvalidRole: any) => {
        const phoneRegex = /^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/;
        const phoneNumberRegex = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
        const formErrors = this.state.formErrors;
        let focusControleName;
        focusControleName = null;
        if (!this.state.company || this.state.company.trim().length === 0) {
            formErrors.company = "This field is required";
            focusControleName = !focusControleName ? "company_Name" : focusControleName;
        }
        if (!this.state.shippingCompanyName || this.state.shippingCompanyName.trim().length === 0) {
            formErrors.shippingCompanyName = "This field is required";
            focusControleName = !focusControleName ? "shippingCompanyName" : focusControleName;
        }
        if (!this.state.totalRooms || this.state.totalRooms.trim().length === 0) {
            formErrors.totalRooms = "This field is required";
            focusControleName = !focusControleName ? "totalRooms" : focusControleName;
        }
        if (!this.state.billToAddress1 || this.state.billToAddress1.trim().length === 0) {
            formErrors.billToAddress1 = "This field is required";
            focusControleName = !focusControleName ? "billToAddress1" : focusControleName;
        }
        if (!this.state.shipToAddress1 || this.state.shipToAddress1.trim().length === 0) {
            formErrors.shipToAddress1 = "This field is required";
            focusControleName = !focusControleName ? "shipToAddress1" : focusControleName;
        }
        if (!this.state.billToAddress2 || this.state.billToAddress2.trim().length === 0) {
            formErrors.billToAddress2 = "This field is required";
            focusControleName = !focusControleName ? "billToAddress2" : focusControleName;
        }
        if (!this.state.shipToAddress2 || this.state.shipToAddress2.trim().length === 0) {
            formErrors.shipToAddress2 = "This field is required";
            focusControleName = !focusControleName ? "shipToAddress2" : focusControleName;
        }
        // if (!this.state.managementCo) {
        //     formErrors.managementCo = "This field is required";
        //     focusControleName = !focusControleName ? "managemnetCompanyDrop" : focusControleName;
        // }
        // else if (this.state.managementCo.trim().length === 0) {
        //     formErrors.managementCo = "This field is required";
        //     focusControleName = !focusControleName ? "managemnetCompanyDrop" : focusControleName;
        // }
        // else if (this.state.managementCo && this.state.managementCo.includes("Select")) {
        //     formErrors.managementCo = "This field is required";
        //     focusControleName = !focusControleName ? "managemnetCompanyDrop" : focusControleName;
        // }
        if (this.state.selectedBillToCountryId === undefined) {
            formErrors.selectedBillToCountry = "This field is required";
            focusControleName = !focusControleName ? "billToCountry" : focusControleName;
        }
        if (this.state.selectedShipToCountryId === undefined) {
            formErrors.selectedShipToCountry = "This field is required";
            focusControleName = !focusControleName ? "shipToCountry" : focusControleName;
        }
        if (this.state.selectedBillToStateId === undefined) {
            formErrors.selectedBillToState = "This field is required";
            focusControleName = !focusControleName ? "billToState" : focusControleName;
        }
        if (this.state.selectedShipToStateId === undefined) {
            formErrors.selectedShipToState = "This field is required";
            focusControleName = !focusControleName ? "shipToState" : focusControleName;
        }
        if (!this.state.billToCity || this.state.billToCity.trim().length === 0) {
            formErrors.billToCity = "This field is required";
            focusControleName = !focusControleName ? "billToCity" : focusControleName;
        }

        if (!this.state.shipToCity || this.state.shipToCity.trim().length === 0) {
            formErrors.shipToCity = "This field is required";
            focusControleName = !focusControleName ? "shipToCity" : focusControleName;
        }
        if (!this.state.billToPostalcode || this.state.billToPostalcode.trim().length === 0) {
            formErrors.billToPostalcode = "This field is required";
            focusControleName = !focusControleName ? "billToPostal" : focusControleName;
        }
        if (!this.state.shipToPostalcode || this.state.shipToPostalcode.trim().length === 0) {
            formErrors.shipToPostalcode = "This field is required";
            focusControleName = !focusControleName ? "shipToPostalCode" : focusControleName;
        }

        if (this.state.fax.trim().length > 0) {
            if (this.state.fax.trim().length !== 10) {
                formErrors.fax = "Fax is invalid";
                focusControleName = !focusControleName ? "mainFax" : focusControleName;
            } else if (!phoneRegex.test(this.state.fax)) {
                formErrors.fax = "Fax is invalid";
                focusControleName = !focusControleName ? "mainFax" : focusControleName;
            }
        }
        if (!this.state.phone || this.state.phone.trim().length === 0) {
            formErrors.phone = "This field is required";
            focusControleName = !focusControleName ? "mainPhone" : focusControleName;
        } else if (this.state.phone.trim().length !== 12 || !phoneNumberRegex.test(this.state.phone)) {
            formErrors.phone = "Phone Number is invalid";
            focusControleName = !focusControleName ? "mainPhone" : focusControleName;
        }
        if (!this.state.gamingProperty || this.state.gamingProperty.trim().length === 0) {
            formErrors.gamingProperty = "This field is required";
            focusControleName = !focusControleName ? "GamingProperty-Yes" : focusControleName;
        }
        if (!this.state.cardCharge || this.state.cardCharge.trim().length === 0) {
            formErrors.cardCharge = "This field is required";
            focusControleName = !focusControleName ? "CardCharge-Yes" : focusControleName;
        }
        if (!this.state.backupCredit || this.state.backupCredit.trim().length === 0) {
            formErrors.backupCredit = "This field is required";
            focusControleName = !focusControleName ? "BackupCreditCard-Yes" : focusControleName;
        }
        if (!this.state.reCertOrTaxExempt || this.state.reCertOrTaxExempt.trim().length === 0) {
            formErrors.reCertOrTaxExempt = "This field is required";
            focusControleName = !focusControleName ? "ReCertTaxExcept-Yes" : focusControleName;
        }
        if (!this.state.question || this.state.question.trim().length === 0) {
            formErrors.question = "This field is required";
            focusControleName = !focusControleName ? "securityQuestion-Yes" : focusControleName;
        }
        if (!this.state.contact1 || this.state.contact1.trim().length === 0) {
            formErrors.contact1 = "This field is required";
            focusControleName = !focusControleName ? "contactTitle" : focusControleName;
        }
        if (!this.state.contactName || this.state.contactName.trim().length === 0) {
            formErrors.contactName = "This field is required";
            focusControleName = !focusControleName ? "apContactFirstName" : focusControleName;
        }
        if (!this.state.contactFirstName || this.state.contactFirstName.trim().length === 0) {
            formErrors.contactFirstName = "This field is required";
            focusControleName = !focusControleName ? "contactFirstName" : focusControleName;
        }
        if (!this.state.apLastName || this.state.apLastName.trim().length === 0) {
            formErrors.apLastName = "This field is required";
            focusControleName = !focusControleName ? "apContactLastName" : focusControleName;
        }
        if (!this.state.contactLastName || this.state.contactLastName.trim().length === 0) {
            formErrors.contactLastName = "This field is required";
            focusControleName = !focusControleName ? "contactLastName" : focusControleName;
        }
        if (!this.state.contactPhone || this.state.contactPhone.trim().length === 0) {
            formErrors.contactPhone = "This field is required";
            focusControleName = !focusControleName ? "apContactPhone" : focusControleName;
        } else if (this.state.contactPhone.trim().length !== 12 || !phoneNumberRegex.test(this.state.contactPhone)) {
            formErrors.contactPhone = "Phone Number is invalid";
            focusControleName = !focusControleName ? "apContactPhone" : focusControleName;
        }
        if (!this.state.contactEmail || this.state.contactEmail.trim().length === 0) {
            formErrors.contactEmail = "This field is required";
            focusControleName = !focusControleName ? "contactEmail" : focusControleName;
        } else if (
            this.state.contactEmail.trim().length > 0 &&
            !/\w+([-+."]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/g.test(this.state.contactEmail)
        ) {
            formErrors.contactEmail = "Email is invalid";
            focusControleName = !focusControleName ? "contactEmail" : focusControleName;
        }
        if (!this.state.electronicemail || this.state.electronicemail.trim().length === 0) {
            formErrors.electronicemail = "This field is required";
            focusControleName = !focusControleName ? "apEmail" : focusControleName;
        } else if (
            this.state.electronicemail.trim().length > 0 &&
            !/\w+([-+."]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/g.test(this.state.electronicemail)
        ) {
            formErrors.electronicemail = "Email is invalid";
            focusControleName = !focusControleName ? "apEmail" : focusControleName;
        }
        if (
            this.state.additionalContactEmail.trim().length > 0 &&
            !/\w+([-+."]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/g.test(this.state.additionalContactEmail)
        ) {
            formErrors.additionalContactEmail = "Email is invalid";
            focusControleName = !focusControleName ? "additionalContactEmail" : focusControleName;
        }
        if (this.props.userName && (this.props.userRole || "").indexOf("TKCCPAdmin") !== -1 && !this.state.classCode) {
            formErrors.classCode = "This field is required";
            focusControleName = !focusControleName ? "classCodeDrop" : focusControleName;
        } else if (
            this.props.userName &&
            (this.props.userRole || "").indexOf("TKCCPAdmin") !== -1 &&
            this.state.classCode.trim().length === 0
        ) {
            formErrors.classCode = "This field is required";
            focusControleName = !focusControleName ? "classCodeDrop" : focusControleName;
        } else if (
            this.props.userName &&
            (this.props.userRole || "").indexOf("TKCCPAdmin") !== -1 &&
            this.state.classCode &&
            this.state.classCode.includes("Select")
        ) {
            formErrors.classCode = "This field is required";
            focusControleName = !focusControleName ? "classCodeDrop" : focusControleName;
        }
        if (this.props.userName && isvalidRole && !this.state.priceList) {
            formErrors.priceList = "This field is required";
            focusControleName = !focusControleName ? "priceListDrop" : focusControleName;
        } else if (this.props.userName && isvalidRole && this.state.priceList.trim().length === 0) {
            formErrors.priceList = "This field is required";
            focusControleName = !focusControleName ? "priceListDrop" : focusControleName;
        } else if (
            this.props.userName &&
            isvalidRole &&
            this.state.priceList &&
            this.state.priceList.includes("Select")
        ) {
            formErrors.priceList = "This field is required";
            focusControleName = !focusControleName ? "priceListDrop" : focusControleName;
        }
        if (this.props.userName && isvalidRole && !this.state.custattr5) {
            formErrors.programList = "This field is required";
            focusControleName = !focusControleName ? "programDrop" : focusControleName;
        } else if (this.props.userName && isvalidRole && this.state.custattr5.trim().length === 0) {
            formErrors.programList = "This field is required";
            focusControleName = !focusControleName ? "programDrop" : focusControleName;
        } else if (
            this.props.userName &&
            isvalidRole &&
            this.state.custattr5 &&
            this.state.custattr5.includes("Select")
        ) {
            formErrors.programList = "This field is required";
            focusControleName = !focusControleName ? "programDrop" : focusControleName;
        }
        if (this.props.userName && isvalidRole && !this.state.collector) {
            formErrors.collectorList = "This field is required";
            focusControleName = !focusControleName ? "collectorDrop" : focusControleName;
        } else if (this.props.userName && isvalidRole && this.state.collector.trim().length === 0) {
            formErrors.collectorList = "This field is required";
            focusControleName = !focusControleName ? "collectorDrop" : focusControleName;
        } else if (
            this.props.userName &&
            isvalidRole &&
            this.state.collector &&
            this.state.collector.includes("Select")
        ) {
            formErrors.collectorList = "This field is required";
            focusControleName = !focusControleName ? "collectorDrop" : focusControleName;
        }
        if (this.props.userName && isvalidRole && !this.state.salesPerson) {
            formErrors.salesRepList = "This field is required";
            focusControleName = !focusControleName ? "salesRepDrop" : focusControleName;
        } else if (this.props.userName && isvalidRole && this.state.salesPerson.trim().length === 0) {
            formErrors.salesRepList = "This field is required";
            focusControleName = !focusControleName ? "salesRepDrop" : focusControleName;
        } else if (
            this.props.userName &&
            isvalidRole &&
            this.state.salesPerson &&
            this.state.salesPerson.includes("Select")
        ) {
            formErrors.salesRepList = "This field is required";
            focusControleName = !focusControleName ? "salesRepDrop" : focusControleName;
        }
        if (this.props.userName && isvalidRole && !this.state.dunningEmailAddress) {
            formErrors.dunningEmailAddress = "This field is required";
            focusControleName = !focusControleName ? "dunningEmailAddress" : focusControleName;
        } else if (this.props.userName && isvalidRole && this.state.dunningEmailAddress.trim().length === 0) {
            formErrors.dunningEmailAddress = "This field is required";
            focusControleName = !focusControleName ? "dunningEmailAddress" : focusControleName;
        } else if (
            this.props.userName &&
            isvalidRole &&
            this.state.dunningEmailAddress.trim().length > 0 &&
            !/\w+([-+."]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/g.test(this.state.dunningEmailAddress)
        ) {
            formErrors.dunningEmailAddress = "Email is invalid";
            focusControleName = !focusControleName ? "dunningEmailAddress" : focusControleName;
        }

        if (
            this.state.dunningEmailAddress &&
            isvalidRole &&
            this.state.dunningEmailAddress.trim().length > 0 &&
            !/\w+([-+."]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/g.test(this.state.dunningEmailAddress)
        ) {
            formErrors.dunningEmailAddress = "Email is invalid";
            focusControleName = !focusControleName ? "dunningEmailAddress" : focusControleName;
        }
        this.setFocus(focusControleName);
        this.setState({ formErrors });
        return formErrors;
    };
    downloadCreditHandler = () => {
        window.open(
            `${window.location.origin}/api/v1/TKCCPCustomerRegistration/downloadfile?fileName=${this.state.creditApplicationFilePath}`,
        );
    };
    downloadTradeAgreementHandler = () => {
        window.open(
            `${window.location.origin}/api/v1/TKCCPCustomerRegistration/downloadfile?fileName=${this.state.tradeAgreementFilePath}`,
        );
    };
    downloadTaxCertificateHandler = () => {
        window.open(
            `${window.location.origin}/api/v1/TKCCPCustomerRegistration/downloadfile?fileName=${this.state.taxCertificateFilePath}`,
        );
    };

    uploadCreditHandler = (event: React.MouseEvent) => {
        this.setState({ isLoading: true });
        if (this.state.creditApplicationFile == null) {
            this.props.toaster.addToast({
                body: siteMessage("This is required field"),
                messageType: "danger",
                timeoutLength: 1500,
            });
            this.setState({ isLoading: false });
            return;
        }

        const regex = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
        const credFile = this.state.creditApplicationFile?.name.split(".");
        if (regex.test(credFile[0])) {
            this.props.toaster.addToast({
                body: siteMessage(
                    "File Name should not contain any special characters like '%' ,'?', '&','@','#' etc.",
                ),
                messageType: "danger",
                timeoutLength: 5000,
            });
            this.setState({ isLoading: false });
            return;
        }

        const fileExtension = getFileExtension(this.state.creditApplicationFile.name);
        const incorrectFileExtension = ["docx", "pdf", "jpeg"].indexOf(fileExtension) === -1;
        if (incorrectFileExtension) {
            this.props.toaster.addToast({
                body: siteMessage("Failed to Upload Document. File must be .docx or .pdf"),
                messageType: "danger",
                timeoutLength: 1500,
            });
            this.setState({ isLoading: false });
            return;
        }

        const formData = new FormData();

        formData.append(
            this.state.referenceNumber,
            this.state.creditApplicationFile,
            this.state.creditApplicationFile.name,
        );
        formData.append("Credit Application", this.state.creditApplicationFile, this.state.creditApplicationFile.name);

        fetch("/api/v1/TKCCPCustomerRegistration/fileupload", {
            method: "POST",
            body: formData,
        }).then(resp => {
            resp.json().then(result => {
                if (result === "success") {
                    this.props.toaster.addToast({
                        body: siteMessage("Document Uploaded Successfully"),
                        messageType: "success",
                        timeoutLength: 1500,
                    });
                    const updatedfilename = this.GetDocumentfilePath(
                        this.state.referenceNumber,
                        this.state.creditApplicationFile.name,
                    );
                    this.setState({ isCreditDocUploaded: true, creditApplicationFilePath: updatedfilename });
                    this.setState({ isLoading: false });
                } else {
                    this.props.toaster.addToast({
                        body: siteMessage("Failed to Upload Document"),
                        messageType: "danger",
                        timeoutLength: 1500,
                    });
                    this.setState({ isCreditDocUploaded: false });
                    this.setState({ isLoading: false });
                }
            });
        });
    };

    uploadTradeHandler = () => {
        this.setState({ isLoading: true });
        if (this.state.tradeApplicationFile == null) {
            this.props.toaster.addToast({
                body: siteMessage("This is required field"),
                messageType: "danger",
                timeoutLength: 1500,
            });
            this.setState({ isLoading: false });
            return;
        }
        const regex = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
        const tradeFile = this.state.tradeApplicationFile?.name.split(".");
        if (regex.test(tradeFile[0])) {
            this.props.toaster.addToast({
                body: siteMessage(
                    "File Name should not contain any special characters like '%' ,'?', '&','@','#' etc.",
                ),
                messageType: "danger",
                timeoutLength: 5000,
            });
            this.setState({ isLoading: false });
            return;
        }
        const fileExtension = getFileExtension(this.state.tradeApplicationFile.name);
        const incorrectFileExtension = ["docx", "pdf", "jpeg"].indexOf(fileExtension) === -1;
        if (incorrectFileExtension) {
            this.props.toaster.addToast({
                body: siteMessage("Failed to Upload Document. File must be .docx or .pdf"),
                messageType: "danger",
                timeoutLength: 1500,
            });
            this.setState({ isLoading: false });
            return;
        }
        const formData = new FormData();
        formData.append(
            this.state.referenceNumber,
            this.state.tradeApplicationFile,
            this.state.tradeApplicationFile.name,
        );
        formData.append("Trade Agreement", this.state.tradeApplicationFile, this.state.tradeApplicationFile.name);

        fetch("/api/v1/TKCCPCustomerRegistration/fileupload", {
            method: "POST",
            body: formData,
        }).then(resp => {
            resp.json().then(result => {
                if (result === "success") {
                    this.props.toaster.addToast({
                        body: siteMessage("Document Uploaded Successfully"),
                        messageType: "success",
                        timeoutLength: 1500,
                    });
                    const updatedfilename = this.GetDocumentfilePath(
                        this.state.referenceNumber,
                        this.state.tradeApplicationFile.name,
                    );
                    this.setState({ isTradeDocUploaded: true, tradeAgreementFilePath: updatedfilename });
                    this.setState({ isLoading: false });
                } else {
                    this.props.toaster.addToast({
                        body: siteMessage("Failed to Upload Document"),
                        messageType: "danger",
                        timeoutLength: 1500,
                    });
                    this.setState({ isTradeDocUploaded: false });
                    this.setState({ isLoading: false });
                }
            });
        });
    };

    uploadTaxHandler = () => {
        this.setState({ isLoading: true });
        if (this.state.taxCertificateFile == null) {
            this.props.toaster.addToast({
                body: siteMessage("This is required field"),
                messageType: "danger",
                timeoutLength: 1500,
            });
            this.setState({ isLoading: false });
            return;
        }
        const regex = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
        const taxFile = this.state.taxCertificateFile?.name.split(".");
        if (regex.test(taxFile[0])) {
            this.props.toaster.addToast({
                body: siteMessage(
                    "File Name should not contain any special characters like '%' ,'?', '&','@','#' etc.",
                ),
                messageType: "danger",
                timeoutLength: 5000,
            });
            this.setState({ isLoading: false });
            return;
        }
        const fileExtension = getFileExtension(this.state.taxCertificateFile.name);
        const incorrectFileExtension = ["docx", "pdf", "jpeg"].indexOf(fileExtension) === -1;
        if (incorrectFileExtension) {
            this.props.toaster.addToast({
                body: siteMessage("Failed to Upload Document. File must be .docx or .pdf"),
                messageType: "danger",
                timeoutLength: 1500,
            });
            this.setState({ isLoading: false });
            return;
        }
        const formData = new FormData();
        formData.append(this.state.referenceNumber, this.state.taxCertificateFile, this.state.taxCertificateFile.name);
        formData.append("Tax Certificate", this.state.taxCertificateFile, this.state.taxCertificateFile.name);

        // "http://courtesyproducts.local.com/api/v1/TKCCPCustomerRegistration/fileupload"
        fetch("/api/v1/TKCCPCustomerRegistration/fileupload", {
            method: "POST",
            body: formData,
        }).then(resp => {
            resp.json().then(result => {
                if (result === "success") {
                    this.props.toaster.addToast({
                        body: siteMessage("Document Uploaded Successfully"),
                        messageType: "success",
                        timeoutLength: 1500,
                    });
                    const updatedfilename = this.GetDocumentfilePath(
                        this.state.referenceNumber,
                        this.state.taxCertificateFile.name,
                    );
                    this.setState({ isTaxDocUploaded: true, taxCertificateFilePath: updatedfilename });
                    this.setState({ isLoading: false });
                } else {
                    this.props.toaster.addToast({
                        body: siteMessage("Failed to Upload Document"),
                        messageType: "danger",
                        timeoutLength: 1500,
                    });
                    this.setState({ isTaxDocUploaded: false });
                    this.setState({ isLoading: false });
                }
            });
        });
    };
    submitHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        const {
            countriesState: { value: countries },
        } = this.props;

        this.setState({ isLoading: true });
        event.preventDefault();
        event.stopPropagation();

        const isvalidRole =
            (this.props.userRole || "").indexOf("TKCCPAccountManager") !== -1 ||
            (this.props.userRole || "").indexOf("TKCCPSalesManager") !== -1 ||
            (this.props.userRole || "").indexOf("TKCCPAdmin") !== -1;

        this.setState({ formSubmitAttempted: true, isSubmitting: true });
        const formErrors = this.validateForm(isvalidRole);
        const e: string[] = [
            formErrors.billToAddress1,
            formErrors.billToAddress2,
            formErrors.billToCity,
            formErrors.billToPostalcode,
            formErrors.shipToAddress1,
            formErrors.shipToAddress2,
            formErrors.shipToCity,
            formErrors.shipToPostalcode,
            formErrors.selectedBillToCountry,
            formErrors.selectedBillToState,
            formErrors.selectedShipToCountry,
            formErrors.selectedShipToState,
            formErrors.phone,
            formErrors.company,
            formErrors.contact1,
            formErrors.contactEmail,
            formErrors.contactPhone,
            formErrors.contactName,
            formErrors.electronicemail,
            formErrors.creditFileError,
            formErrors.taxFileError,
            formErrors.tradeFileError,
            formErrors.fax,
            formErrors.apLastName,
            // formErrors.managementCo,
            formErrors.priceList,
            formErrors.classCode,
            formErrors.dunningEmailAddress,
            formErrors.programList,
            formErrors.collectorList,
            formErrors.salesRepList,
            formErrors.totalRooms,
            formErrors.gamingProperty,
            formErrors.cardCharge,
            formErrors.backupCredit,
            formErrors.reCertOrTaxExempt,
            formErrors.question,
            formErrors.contactFirstName,
            formErrors.contactLastName,
            formErrors.shippingCompanyName,
            formErrors.additionalContactEmail,
        ];

        if (!this.state.selectedShipToStateId) {
            this.setState({ isLoading: false });
            return;
        }
        if (!this.state.selectedShipToCountryId) {
            this.setState({ isLoading: false });
            return;
        }
        if (!this.state.selectedBillToCountryId) {
            this.setState({ isLoading: false });
            return;
        }
        if (!this.state.selectedBillToStateId) {
            this.setState({ isLoading: false });
            return;
        }
        // if (this.state.managementCo.includes("Select")) {
        //     this.setState({isLoading:false});
        //     return;
        // }
        if (
            this.props.userRole &&
            (this.props.userRole || "").indexOf("TKCCPAdmin") !== -1 &&
            this.state.classCode.includes("Select")
        ) {
            this.setState({ isLoading: false });
            return;
        }
        if (this.props.userRole && isvalidRole && this.state.priceList.includes("Select")) {
            this.setState({ isLoading: false });
            return;
        }
        if (this.props.userRole && isvalidRole && this.state.custattr5.includes("Select")) {
            this.setState({ isLoading: false });
            return;
        }
        if (this.props.userRole && isvalidRole && this.state.collector.includes("Select")) {
            this.setState({ isLoading: false });
            return;
        }
        if (this.props.userRole && isvalidRole && this.state.salesPerson.includes("Select")) {
            this.setState({ isLoading: false });
            return;
        }
        let error: string[] = [];
        error = e.filter(
            y =>
                y.valueOf().includes("This") ||
                y.valueOf().includes("Email") ||
                y.valueOf().includes("Phone") ||
                y.valueOf().includes("Fax"),
        );

        const securityQues = `${this.props.securityQuestion}/${this.state.question}`;

        const getCountry = countries;
        const getSelectedBillToCountry = getCountry?.find(o => o.id === this.state.selectedBillToCountryId);
        const getSelectedBillToState = getSelectedBillToCountry
            ? getSelectedBillToCountry?.states?.find(o => o.id === this.state.selectedBillToStateId)
            : undefined;
        const getSelectedShipToCountry = getCountry?.find(o => o.id === this.state.selectedShipToCountryId);
        const getSelectedShipToState = getSelectedShipToCountry
            ? getSelectedShipToCountry?.states?.find(o => o.id === this.state.selectedShipToStateId)
            : undefined;

        // this.setState({
        //     selectedBillToState: getSelectedBillToState,
        //     selectedBillToCountry: getSelectedBillToCountry,
        //     selectedShipToState: getSelectedShipToState,
        //     selectedShipToCountry: getSelectedShipToCountry,
        // });

        if (error.length === 0) {
            this.props.addCustomer({
                apiParameter: {
                    newCustomerModel: {
                        billToModel: {
                            id: this.state.billToId,
                            address1: this.state.billToAddress1,
                            address2: this.state.billToAddress2,
                            state: getSelectedBillToState,
                            country: getSelectedBillToCountry,
                            city: this.state.billToCity,
                            postalCode: this.state.billToPostalcode,
                            phone: this.state.phone,
                            fax: this.state.fax,
                            firstname: this.state.contact1,
                            email: this.state.contactEmail,
                            companyName: this.state.company,
                        },
                        shipToModel: {
                            id: this.state.shipToId,
                            address1: this.state.shipToAddress1,
                            address2: this.state.shipToAddress2,
                            state: getSelectedShipToState,
                            country: getSelectedShipToCountry,
                            city: this.state.shipToCity,
                            postalCode: this.state.shipToPostalcode,
                            phone: this.state.phone,
                            fax: this.state.fax,
                            firstname: this.state.contact2,
                            email: this.state.contactEmail,
                            companyName: this.state.shippingCompanyName,
                        },
                        properties: {
                            buyingGroup: this.state.buyingGroup,
                            gamingProperty: this.state.gamingProperty,
                            totalRooms: this.state.totalRooms,
                            cardCharge: this.state.cardCharge,
                            backupCredit: this.state.backupCredit,
                            reCertOrTaxExempt: this.state.reCertOrTaxExempt,
                            contactName: this.state.contactName,
                            avendraID: this.state.avendraID,
                            // taxable: this.state.taxable,
                            electronicemail: this.state.electronicemail,
                            contactPhone: this.state.contactPhone,
                            creditFile: this.state.creditApplicationFile?.name,
                            tradeFile: this.state.tradeApplicationFile?.name,
                            taxFile: this.state.taxCertificateFile?.name,
                            securityQuestion: securityQues,
                            managementCo: this.state.managementCo,
                            brands: this.state.brands,
                            contactFirstName: this.state.contactFirstName,
                            contactLastName: this.state.contactLastName,
                            apLastName: this.state.apLastName,
                            adminApproved: this.state.adminApproved,
                            salesManagerApproved: this.state.salesManagerApproved,
                            accountManagerApproved: this.state.accountManagerApproved,
                            classCode: this.state.classCode,
                            collector: this.state.collector,
                            priceList: this.state.priceList,
                            custAttr5: this.state.custattr5,
                            csrNotes: this.state.csrNotes,
                            dunningEmailAddress: this.state.dunningEmailAddress,
                            creditSpendingLimit: this.state.creditSpendingLimit,
                            numberOfServers: this.state.numberOfServers,
                            coffeeMakerStartDate: this.state.coffeeMakersStartDate,
                            salesPerson: this.state.salesPerson,
                            notes: this.state.notes,
                            additionalContactTitle: this.state.additionalContactTitle,
                            additionalContactFirstName: this.state.additionalContactFirstName,
                            additionalContactLastName: this.state.additionalContactLastName,
                            additionalContactEmail: this.state.additionalContactEmail,
                            sameAsBilling: this.state.sameAsBilling,
                        },
                    },
                } as unknown as AddCustomerApiParameter,
                onSuccess: response => {
                    if (response) {
                        this.props.toaster.addToast({
                            body: siteMessage("Account Information Saved"),
                            messageType: "success",
                            timeoutLength: 9000,
                        });
                        this.resetForm();
                        this.refrenceNumberHandler("", response);
                    } else {
                        this.props.toaster.addToast({
                            body: siteMessage("Account Information Update Successfully"),
                            messageType: "success",
                            timeoutLength: 9000,
                        });
                    }
                    this.setState({ isLoading: false });
                },
                onError: () => {
                    this.setState({ isLoading: false });
                    this.props.toaster.addToast({
                        body: siteMessage("Error occured while creating Account"),
                        messageType: "danger",
                        timeoutLength: 9000,
                    });
                },
            });
        } else {
            this.setState({
                isSubmitting: false,
                isLoading: false,
            });
        }
    };
    render() {
        const {
            countriesState: { value: countries },
            securityQuestion,
            userRole,
            userName,
        } = this.props;
        const {
            billToId,
            shipToId,
            // selectedBillToCountry,
            // selectedBillToState,
            // selectedShipToCountry,
            // selectedShipToState,
            billToAddress1,
            billToAddress2,
            billToCity,
            billToPostalcode,
            shipToAddress1,
            shipToAddress2,
            shipToCity,
            shipToPostalcode,
            phone,
            fax,
            company,
            gamingProperty,
            contact1,
            contact2,
            totalRooms,
            buyingGroup,
            selectedBillToCountryId,
            selectedBillToStateId,
            selectedShipToStateId,
            selectedShipToCountryId,
            managementCo,
            brands,
            cardCharge,
            backupCredit,
            reCertOrTaxExempt,
            contactName,
            contactEmail,
            avendraID,
            // taxable,
            contactPhone,
            electronicemail,
            formErrors,
            incorrectCreditFileExtension,
            incorrectTaxFileExtension,
            incorrectTradeFileExtension,
            creditApplicationFile,
            taxCertificateFile,
            tradeApplicationFile,
            question,
            showCustomerInformationBlock,
            referenceNumberMessage,
            referenceNumber,
            IsCustomerExist,
            contactFirstName,
            contactLastName,
            apLastName,
            isCreditDocUploaded,
            isTradeDocUploaded,
            isTaxDocUploaded,
            isManagerApproved,
            buyingGroupAttributeLabel,
            isBuyingGrpAttVisible,
            adminApproved,
            salesManagerApproved,
            accountManagerApproved,
            classCode,
            collector,
            priceList,
            custattr5,
            csrNotes,
            salesPerson,
            customerAttribute,
            salesPersonOptions,
            buyingGroupOptions,
            managementCoOptions,
            brandsOptions,
            priceListOptions,
            classCodesOptions,
            collectorsOptions,
            programsOptions,
            coffeeMakersStartDate,
            dunningEmailAddress,
            numberOfServers,
            creditSpendingLimit,
            isLoading,
            notes,
            shippingCompanyName,
            additionalContactTitle,
            additionalContactFirstName,
            additionalContactLastName,
            additionalContactEmail,
            creditApplicationFilePath,
            tradeAgreementFilePath,
            taxCertificateFilePath,
            sameAsBilling,
        } = this.state;

        if (!countries) {
            return null;
        }

        const getCountry1 = countries;

        const getSelectedBillToCountry1 = getCountry1?.find(o => o.id === selectedBillToCountryId);
        // const getSelectedBillToState1 = getSelectedBillToCountry1 ? getSelectedBillToCountry1?.states?.find(o => o.id === selectedBillToStateId) : undefined;
        const getSelectedShipToCountry1 = getCountry1?.find(o => o.id === selectedShipToCountryId);
        // const getSelectedShipToState1 = getSelectedShipToCountry1 ? getSelectedShipToCountry1?.states?.find(o => o.id === selectedShipToStateId) : undefined;

        const displayAsperRole =
            (this.props.userRole || "").indexOf("TKCCPAccountManager") !== -1 ||
            (this.props.userRole || "").indexOf("TKCCPSalesManager") !== -1 ||
            (this.props.userRole || "").indexOf("TKCCPAdmin") !== -1;

        const displayFinApproveCheckBox =
            (this.props.userRole || "").indexOf("TKCCPSalesManager") !== -1 ||
            (this.props.userRole || "").indexOf("TKCCPAdmin") !== -1;
        const displayAccApproveCheckBox =
            (this.props.userRole || "").indexOf("TKCCPAccountManager") !== -1 ||
            (this.props.userRole || "").indexOf("TKCCPSalesManager") !== -1 ||
            (this.props.userRole || "").indexOf("TKCCPAdmin") !== -1;

        return (
            <>
                <StyledWrapper {...styles.firstWrapper}>
                    <Typography {...styles.mainHeading}>
                        {translate("New Account Customer Form")}
                        <span style={{ fontSize: "14px", marginLeft: "4px" }}>(*Required Field)</span>
                    </Typography>
                    <Typography {...styles.createButtonText}>{siteMessage("signin_customerAccount_text")}</Typography>
                </StyledWrapper>
                <StyledForm {...styles.form}>
                    <LoadingOverlay loading={isLoading}>
                        <TabGroup current={IsCustomerExist ? "Documents" : "Information"}>
                            <Tab tabKey="Information" headline="Information">
                                {showCustomerInformationBlock ? (
                                    <>
                                        <GridContainer {...styles.mainGridContainer}>
                                            <GridItem {...styles.commonTwoColumnGridItem}>
                                                <GridContainer>
                                                    <GridItem {...styles.userGridBillingItem}>
                                                        <GridContainer {...styles.userGridContainer}>
                                                            <GridItem
                                                                {...styles.userNameGridItem}
                                                                className="bottom-spacing"
                                                            >
                                                                <Typography {...styles.heading}>
                                                                    {translate("Billing Address")}
                                                                </Typography>
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    label={translate("Company Name")}
                                                                    id="company_Name"
                                                                    value={company}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.companyChangeHandler}
                                                                    error={formErrors.company}
                                                                    required
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="billToAddress1"
                                                                    label={translate("Address 1")}
                                                                    name="billToAddress1"
                                                                    value={billToAddress1}
                                                                    data-test-selector="createNewAccount_address1"
                                                                    onChange={this.billToAddress1Handler}
                                                                    error={formErrors.billToAddress1}
                                                                    required
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="billToAddress2"
                                                                    label={translate("Address 2")}
                                                                    name="billToAddress2"
                                                                    value={billToAddress2}
                                                                    data-test-selector="createNewAccount_address2"
                                                                    onChange={this.billToAddress2Handler}
                                                                    error={formErrors.billToAddress2}
                                                                    placeholder="Attn:"
                                                                    required
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <Select
                                                                    id="billToCountry"
                                                                    label={translate("Country")}
                                                                    value={selectedBillToCountryId || ""}
                                                                    onChange={this.handleSelectCountry}
                                                                    error={formErrors.selectedBillToCountry}
                                                                    required
                                                                >
                                                                    <option value="">Select your country</option>
                                                                    {countries.map(o => (
                                                                        <option value={o.id} key={o.id}>
                                                                            {o.name}
                                                                        </option>
                                                                    ))}
                                                                </Select>
                                                            </GridItem>

                                                            {getSelectedBillToCountry1?.states &&
                                                                getSelectedBillToCountry1.states.length > 0 && (
                                                                    <GridItem {...styles.userNameGridItem}>
                                                                        <Select
                                                                            id="billToState"
                                                                            label={translate("State")}
                                                                            value={selectedBillToStateId || ""}
                                                                            onChange={this.handleSelectState}
                                                                            error={formErrors.selectedBillToState}
                                                                            required
                                                                        >
                                                                            <option value="">
                                                                                {translate("Select your state")}
                                                                            </option>
                                                                            {getSelectedBillToCountry1.states?.map(
                                                                                o => (
                                                                                    <option value={o.id} key={o.id}>
                                                                                        {o.name}
                                                                                    </option>
                                                                                ),
                                                                            )}
                                                                        </Select>
                                                                    </GridItem>
                                                                )}

                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="billToCity"
                                                                    label={translate("City")}
                                                                    value={billToCity}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.billToCityHandler}
                                                                    error={formErrors.billToCity}
                                                                    required
                                                                    onInput={(e: ChangeEvent<HTMLInputElement>) => {
                                                                        e.target.value = e.target.value.replace(
                                                                            /[0-9]*/g,
                                                                            "",
                                                                        );
                                                                    }}
                                                                />
                                                            </GridItem>

                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="billToPostal"
                                                                    label={translate("PostalCode")}
                                                                    // pattern="[+-]?\d+(?:[.,]\d+)?"
                                                                    value={billToPostalcode}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.billToPostalCodeHandler}
                                                                    error={formErrors.billToPostalcode}
                                                                    required
                                                                    onInput={(e: ChangeEvent<HTMLInputElement>) => {
                                                                        e.target.value = formatZipCode(e.target.value);
                                                                    }}
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="mainPhone"
                                                                    label={translate("Main Phone")}
                                                                    value={phone}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.phoneHandler}
                                                                    error={formErrors.phone}
                                                                    required
                                                                />
                                                            </GridItem>
                                                        </GridContainer>
                                                    </GridItem>
                                                    <GridItem {...styles.userGridShippingItem}>
                                                        <GridContainer {...styles.userGridContainer}>
                                                            <GridItem
                                                                {...styles.userNameGridItem}
                                                                className="shipping-address-grid"
                                                            >
                                                                <Typography {...styles.heading}>
                                                                    {translate("Shipping Address")}
                                                                </Typography>
                                                                <CheckboxGroup {...styles.billingCheckbox}>
                                                                    <Checkbox
                                                                        onChange={this.sameAsBillingFlagHandler}
                                                                        checked={sameAsBilling}
                                                                        data-test-selector="SameAsBilling"
                                                                        // disabled={!displayAccApproveCheckBox}
                                                                    >
                                                                        {translate("Same as Billing")}
                                                                    </Checkbox>
                                                                </CheckboxGroup>
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    label={translate("Company Name")}
                                                                    id="shippingCompanyName"
                                                                    value={shippingCompanyName}
                                                                    data-test-selector="createAccount_shippingCompanyName"
                                                                    disabled={sameAsBilling}
                                                                    onChange={this.shippingCompanyNameChangeHandler}
                                                                    error={formErrors.shippingCompanyName}
                                                                    required
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="shipToAddress1"
                                                                    label={translate("Address 1")}
                                                                    value={shipToAddress1}
                                                                    data-test-selector="createNewAccount_address1"
                                                                    disabled={sameAsBilling}
                                                                    onChange={this.shipToAddress1Handler}
                                                                    error={formErrors.shipToAddress1}
                                                                    required
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="shipToAddress2"
                                                                    label={translate("Address 2")}
                                                                    value={shipToAddress2}
                                                                    data-test-selector="createNewAccount_address2"
                                                                    // disabled={sameAsBilling}
                                                                    onChange={this.shipToAddress2Handler}
                                                                    error={formErrors.shipToAddress2}
                                                                    required
                                                                    placeholder="Attn:"
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <Select
                                                                    id="shipToCountry"
                                                                    label={translate("Country")}
                                                                    value={selectedShipToCountryId || ""}
                                                                    onChange={this.handleSelectShipToCountry}
                                                                    disabled={sameAsBilling}
                                                                    error={formErrors.selectedShipToCountry}
                                                                    required
                                                                >
                                                                    <option value="">Select your country</option>
                                                                    {countries.map(o => (
                                                                        <option value={o.id} key={o.id}>
                                                                            {o.name}
                                                                        </option>
                                                                    ))}
                                                                </Select>
                                                            </GridItem>
                                                            {getSelectedShipToCountry1?.states &&
                                                                getSelectedShipToCountry1.states.length > 0 && (
                                                                    <GridItem {...styles.userNameGridItem}>
                                                                        <Select
                                                                            id="shipToState"
                                                                            label={translate("State")}
                                                                            value={selectedShipToStateId || ""}
                                                                            onChange={this.handleSelectShipToState}
                                                                            disabled={sameAsBilling}
                                                                            error={formErrors.selectedShipToState}
                                                                            required
                                                                        >
                                                                            <option value="">
                                                                                {translate("Select your state")}
                                                                            </option>
                                                                            {getSelectedShipToCountry1.states?.map(
                                                                                o => (
                                                                                    <option value={o.id} key={o.id}>
                                                                                        {o.name}
                                                                                    </option>
                                                                                ),
                                                                            )}
                                                                        </Select>
                                                                    </GridItem>
                                                                )}

                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="shipToCity"
                                                                    label={translate("City")}
                                                                    value={shipToCity}
                                                                    data-test-selector="createAccount_userName"
                                                                    disabled={sameAsBilling}
                                                                    onChange={this.shipToCityHandler}
                                                                    error={formErrors.shipToCity}
                                                                    required
                                                                    type="text"
                                                                    onInput={(e: ChangeEvent<HTMLInputElement>) => {
                                                                        e.target.value = e.target.value.replace(
                                                                            /[0-9]*/g,
                                                                            "",
                                                                        );
                                                                    }}
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="shipToPostalCode"
                                                                    label={translate("PostalCode")}
                                                                    value={shipToPostalcode}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.shipToPostalCodeHandler}
                                                                    disabled={sameAsBilling}
                                                                    error={formErrors.shipToPostalcode}
                                                                    required
                                                                    onInput={(e: ChangeEvent<HTMLInputElement>) => {
                                                                        e.target.value = formatZipCode(e.target.value);
                                                                    }}
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="mainFax"
                                                                    label={translate("Main Fax")}
                                                                    value={fax}
                                                                    minLength={10}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.faxChangeHandler}
                                                                    error={formErrors.fax}
                                                                />
                                                            </GridItem>

                                                            {/* <GridItem {...styles.userNameGridItem}>
                                                            <TextField
                                                                {...styles.userNameTextField}
                                                                label={translate("2nd Contact/Title")}
                                                                value={contact2}
                                                                data-test-selector="createAccount_userName"
                                                                onChange={this.contact2Handler}
                                                            />
                                                        </GridItem> */}
                                                        </GridContainer>
                                                    </GridItem>
                                                </GridContainer>
                                                <GridContainer {...styles.commonTwoColumnGridContainer}>
                                                    <GridItem {...styles.userGridContactItem}>
                                                        <GridContainer {...styles.userPurchaseGridContainer}>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <Typography {...styles.heading}>
                                                                    {translate("Purchasing Contact")}
                                                                </Typography>
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="contactTitle"
                                                                    label={translate("Contact/Title")}
                                                                    value={contact1}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.contact1Handler}
                                                                    error={formErrors.contact1}
                                                                    required
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="contactFirstName"
                                                                    label={translate("Contact First Name")}
                                                                    value={contactFirstName}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.contactFirstNameChangeHandler}
                                                                    error={formErrors.contactFirstName}
                                                                    required
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="contactLastName"
                                                                    label={translate("Contact Last Name ")}
                                                                    value={contactLastName}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.contactLastNameChangeHandler}
                                                                    error={formErrors.contactLastName}
                                                                    required
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.emailGridItem}>
                                                                <TextField
                                                                    {...styles.emailTextField}
                                                                    id="contactEmail"
                                                                    type="email"
                                                                    label={translate("Contact Email")}
                                                                    onChange={this.emailChangeHandler}
                                                                    value={contactEmail}
                                                                    data-test-selector="createAccount_email"
                                                                    required
                                                                    error={formErrors.contactEmail}
                                                                />
                                                            </GridItem>
                                                        </GridContainer>
                                                    </GridItem>
                                                    <GridItem {...styles.userGridContactItem}>
                                                        <GridContainer {...styles.userGridContainer}>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <Typography {...styles.heading}>
                                                                    {translate("AP Contact Details")}
                                                                </Typography>
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="apContactFirstName"
                                                                    label={translate("A/P Contact First Name")}
                                                                    value={contactName}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.contactNameChangeHandler}
                                                                    error={formErrors.contactName}
                                                                    required
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="apContactLastName"
                                                                    label={translate("A/P Contact Last Name")}
                                                                    value={apLastName}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.apContactLastNameChangeHandler}
                                                                    error={formErrors.apLastName}
                                                                    required
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="apContactPhone"
                                                                    label={translate("A/P Contact Phone Number")}
                                                                    value={contactPhone}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.contactPhoneHandler}
                                                                    error={formErrors.contactPhone}
                                                                    required
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.emailGridItem}>
                                                                <TextField
                                                                    {...styles.emailTextField}
                                                                    id="apEmail"
                                                                    type="email"
                                                                    label={translate("AP Contact Email Address")}
                                                                    onChange={this.electronicEmailHandler}
                                                                    value={electronicemail}
                                                                    data-test-selector="createAccount_email"
                                                                    required
                                                                    error={formErrors.electronicemail}
                                                                />
                                                            </GridItem>
                                                        </GridContainer>
                                                    </GridItem>
                                                    <GridItem {...styles.notesGridItem}>
                                                        <TextArea
                                                            id="notes"
                                                            label={translate("Notes")}
                                                            onChange={this.notesChangeHandler}
                                                            value={notes}
                                                            data-test-selector="createAccount_notes"
                                                        />
                                                    </GridItem>
                                                </GridContainer>
                                            </GridItem>
                                            <GridItem {...styles.userOthersGridItem}>
                                                <GridContainer>
                                                    <GridItem {...styles.userOtherGridItem}>
                                                        <GridContainer {...styles.userGridContainer}>
                                                            <GridItem
                                                                {...styles.userNameGridItem}
                                                                className="bottom-spacing-other"
                                                            >
                                                                <Typography {...styles.heading}>
                                                                    {translate("Others")}
                                                                </Typography>
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <TextField
                                                                    {...styles.userNameTextField}
                                                                    id="totalRooms"
                                                                    label={translate("Total # Rooms")}
                                                                    value={totalRooms}
                                                                    data-test-selector="createAccount_userName"
                                                                    onChange={this.totalRoomsChangeHandler}
                                                                    required
                                                                    error={formErrors.totalRooms}
                                                                />
                                                            </GridItem>
                                                            {/* <GridItem {...styles.userNameGridItem}>
                                                            <TextField
                                                                {...styles.userNameTextField}
                                                                id="taxable"
                                                                label={translate("Taxable")}
                                                                value={taxable}
                                                                data-test-selector="createAccount_userName"
                                                                onChange={this.taxableHandler}
                                                            />
                                                        </GridItem> */}
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <DynamicDropdown
                                                                    {...styles.managementCoSelectorDynamicDropdown}
                                                                    // uid="managemnetCompanyDrop"
                                                                    inputId="managemnetCo"
                                                                    label={translate("Management Company")}
                                                                    onSelectionChange={this.managementCoChangeHandler}
                                                                    selected={managementCo}
                                                                    options={managementCoOptions}
                                                                    placeholder={translate("Select Management Company")}
                                                                    data-test-selector="productSelector_search"
                                                                    // error={formErrors.managementCo}
                                                                    // required
                                                                />
                                                            </GridItem>

                                                            <GridItem {...styles.userNameGridItem}>
                                                                <DynamicDropdown
                                                                    {...styles.brandsSelectorDynamicDropdown}
                                                                    label={translate("Brands")}
                                                                    onSelectionChange={this.brandsChangeHandler}
                                                                    selected={brands}
                                                                    options={brandsOptions}
                                                                    placeholder={translate("Select Brand")}
                                                                    data-test-selector="productSelector_search"
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.userNameGridItem}>
                                                                <DynamicDropdown
                                                                    {...styles.buyingGroupSelectorDynamicDropdown}
                                                                    label={translate("Buying Group")}
                                                                    onSelectionChange={this.buyingGroupChangeHandler}
                                                                    selected={buyingGroup}
                                                                    options={buyingGroupOptions}
                                                                    placeholder={translate("Select Buying Group")}
                                                                    data-test-selector="productSelector_search"
                                                                />
                                                            </GridItem>
                                                            {isBuyingGrpAttVisible && (
                                                                <GridItem {...styles.userNameGridItem}>
                                                                    <TextField
                                                                        {...styles.userNameTextField}
                                                                        label={translate(buyingGroupAttributeLabel)}
                                                                        value={avendraID}
                                                                        disabled={!isBuyingGrpAttVisible}
                                                                        data-test-selector="createAccount_userName"
                                                                        onChange={this.avendraIDChangeHandler}
                                                                    />
                                                                </GridItem>
                                                            )}

                                                            <GridItem
                                                                {...styles.userRadioBtnGridItem}
                                                                className={
                                                                    formErrors.gamingProperty ? "error-field" : ""
                                                                }
                                                            >
                                                                <RadioGroup
                                                                    id="gamingProperty"
                                                                    label="Gaming Property?"
                                                                    name="GamingProperty"
                                                                    onChangeHandler={this.gamingPropertyHandler}
                                                                    value={gamingProperty}
                                                                    {...styles.userRadioBtn}
                                                                    error={formErrors.gamingProperty}
                                                                    required
                                                                >
                                                                    {this.options1.map(option => (
                                                                        <Radio key={option}>{option}</Radio>
                                                                    ))}
                                                                </RadioGroup>
                                                            </GridItem>

                                                            <GridItem
                                                                {...styles.userRadioBtnGridItem}
                                                                className={formErrors.cardCharge ? "error-field" : ""}
                                                            >
                                                                <RadioGroup
                                                                    id="cardCharge"
                                                                    label="Charge card each order?"
                                                                    name="CardCharge"
                                                                    onChangeHandler={this.cardChargeChangeHandler}
                                                                    value={cardCharge}
                                                                    {...styles.userRadioBtn}
                                                                    error={formErrors.cardCharge}
                                                                    required
                                                                >
                                                                    {options1.map(option => (
                                                                        <Radio key={option}>{option}</Radio>
                                                                    ))}
                                                                </RadioGroup>
                                                            </GridItem>

                                                            <GridItem
                                                                {...styles.userRadioBtnGridItem}
                                                                className={formErrors.backupCredit ? "error-field" : ""}
                                                            >
                                                                <RadioGroup
                                                                    id="backupCredit"
                                                                    label="Back-up Credit Card?"
                                                                    name="BackupCreditCard"
                                                                    onChangeHandler={this.bachupCreditCardHandler}
                                                                    value={backupCredit}
                                                                    {...styles.userRadioBtn}
                                                                    error={formErrors.backupCredit}
                                                                    required
                                                                >
                                                                    {options1.map(option => (
                                                                        <Radio key={option}>{option}</Radio>
                                                                    ))}
                                                                </RadioGroup>
                                                            </GridItem>
                                                            <GridItem
                                                                {...styles.userRadioBtnGridItem}
                                                                className={
                                                                    formErrors.reCertOrTaxExempt ? "error-field" : ""
                                                                }
                                                            >
                                                                <RadioGroup
                                                                    id="reCertOrTaxExempt"
                                                                    label="Re-cert or Tax Exempt?"
                                                                    name="ReCertTaxExcept"
                                                                    onChangeHandler={this.reCertTaxExceptHandler}
                                                                    value={reCertOrTaxExempt}
                                                                    {...styles.userRadioBtn}
                                                                    error={formErrors.reCertOrTaxExempt}
                                                                    required
                                                                >
                                                                    {options1.map(option => (
                                                                        <Radio key={option}>{option}</Radio>
                                                                    ))}
                                                                </RadioGroup>
                                                            </GridItem>
                                                            <GridItem
                                                                {...styles.userRadioBtnGridItem}
                                                                className={formErrors.question ? "error-field" : ""}
                                                            >
                                                                <RadioGroup
                                                                    id="question"
                                                                    label={securityQuestion}
                                                                    name="securityQuestion"
                                                                    onChangeHandler={this.securityQuestionHandler}
                                                                    value={question}
                                                                    {...styles.userRadioBtn}
                                                                    error={formErrors.question}
                                                                    required
                                                                >
                                                                    {options1.map(option => (
                                                                        <Radio key={option}>{option}</Radio>
                                                                    ))}
                                                                </RadioGroup>
                                                            </GridItem>
                                                        </GridContainer>
                                                    </GridItem>
                                                    <GridItem {...styles.userOtherGridItem}>
                                                        <GridContainer>
                                                            <GridItem {...styles.userGridAdditionalContactItem}>
                                                                <GridContainer {...styles.userGridContainer}>
                                                                    <GridItem {...styles.userNameGridItem}>
                                                                        <Typography {...styles.heading}>
                                                                            {translate("Additional Contact")}
                                                                        </Typography>
                                                                    </GridItem>
                                                                    <GridItem {...styles.userNameGridItem}>
                                                                        <TextField
                                                                            {...styles.userNameTextField}
                                                                            id="additionalContactTitle"
                                                                            label={translate("Contact/Title")}
                                                                            value={additionalContactTitle}
                                                                            data-test-selector="createAccount_additionalContactTitle"
                                                                            onChange={
                                                                                this.additionalContactTitleChangeHandler
                                                                            }
                                                                        />
                                                                    </GridItem>
                                                                    <GridItem {...styles.userNameGridItem}>
                                                                        <TextField
                                                                            {...styles.userNameTextField}
                                                                            id="additionalContactFirstName"
                                                                            label={translate("Contact First Name")}
                                                                            value={additionalContactFirstName}
                                                                            data-test-selector="createAccount_additionalContactFirstName"
                                                                            onChange={
                                                                                this
                                                                                    .additionalContactFirstNameChangeHandler
                                                                            }
                                                                        />
                                                                    </GridItem>
                                                                    <GridItem {...styles.userNameGridItem}>
                                                                        <TextField
                                                                            {...styles.userNameTextField}
                                                                            id="additionalContactLastName"
                                                                            label={translate("Contact Last Name")}
                                                                            value={additionalContactLastName}
                                                                            data-test-selector="createAccount_additionalContactLastName"
                                                                            onChange={
                                                                                this
                                                                                    .additionalContactLastNameChangeHandler
                                                                            }
                                                                        />
                                                                    </GridItem>
                                                                    <GridItem {...styles.emailGridItem}>
                                                                        <TextField
                                                                            {...styles.emailTextField}
                                                                            id="additionalContactEmail"
                                                                            type="email"
                                                                            label={translate("Contact Email")}
                                                                            onChange={
                                                                                this.additionalContactEmailHandler
                                                                            }
                                                                            value={additionalContactEmail}
                                                                            data-test-selector="createAccount_additionalContactEmail"
                                                                            error={formErrors.additionalContactEmail}
                                                                        />
                                                                    </GridItem>
                                                                </GridContainer>
                                                            </GridItem>
                                                        </GridContainer>
                                                    </GridItem>
                                                </GridContainer>
                                            </GridItem>
                                            <GridItem {...styles.internalUserGridItem}>
                                                {this.props.userName && displayAsperRole && (
                                                    <GridContainer {...styles.internalUseContainer}>
                                                        <GridItem {...styles.userNameGridItem}>
                                                            <Typography {...styles.heading}>
                                                                {translate("Internal use only")}
                                                            </Typography>
                                                        </GridItem>
                                                        <GridItem {...styles.internalUseGridItem}>
                                                            <GridContainer>
                                                                <GridItem {...styles.internalFormFieldGridItem}>
                                                                    <DynamicDropdown
                                                                        {...styles.classCodeSelectorDynamicDropdown}
                                                                        uid="classCodeDrop"
                                                                        inputId="classCode"
                                                                        label={translate("Class Codes")}
                                                                        onSelectionChange={this.classCodeChangeHandler}
                                                                        selected={classCode}
                                                                        options={classCodesOptions}
                                                                        disabled={
                                                                            !(
                                                                                (this.props.userRole || "").indexOf(
                                                                                    "TKCCPAdmin",
                                                                                ) !== -1
                                                                            )
                                                                        }
                                                                        placeholder={translate("Select Class Codes")}
                                                                        data-test-selector="productSelector_search"
                                                                        error={formErrors.classCode}
                                                                        required
                                                                    />
                                                                </GridItem>
                                                                <GridItem {...styles.internalFormFieldGridItem}>
                                                                    <DynamicDropdown
                                                                        {...styles.priceListSelectorDynamicDropdown}
                                                                        label={translate("Price List")}
                                                                        uid="priceListDrop"
                                                                        inputId="priceList"
                                                                        onSelectionChange={this.priceListChangeHandler}
                                                                        selected={priceList}
                                                                        options={priceListOptions}
                                                                        placeholder={translate("Select Price List")}
                                                                        data-test-selector="productSelector_search"
                                                                        error={formErrors.priceList}
                                                                        required
                                                                    />
                                                                </GridItem>
                                                                <GridItem {...styles.internalFormFieldGridItem}>
                                                                    <DynamicDropdown
                                                                        {...styles.attr5SelectorDynamicDropdown}
                                                                        label={translate("Program")}
                                                                        uid="programDrop"
                                                                        inputId="programList"
                                                                        onSelectionChange={this.attribute5ChangeHandler}
                                                                        selected={custattr5}
                                                                        options={programsOptions}
                                                                        placeholder={translate("Select Program")}
                                                                        data-test-selector="productSelector_search"
                                                                        error={formErrors.programList}
                                                                        required
                                                                    />
                                                                </GridItem>
                                                                <GridItem {...styles.internalFormFieldGridItem}>
                                                                    <DynamicDropdown
                                                                        {...styles.collectorSelectorDynamicDropdown}
                                                                        label={translate("Collector")}
                                                                        uid="collectorDrop"
                                                                        inputId="collectorList"
                                                                        onSelectionChange={this.collectorChangeHandler}
                                                                        selected={collector}
                                                                        options={collectorsOptions}
                                                                        placeholder={translate("Select Collector")}
                                                                        data-test-selector="productSelector_search"
                                                                        error={formErrors.collectorList}
                                                                        required
                                                                    />
                                                                </GridItem>
                                                                <GridItem {...styles.internalFormFieldGridItem}>
                                                                    <DynamicDropdown
                                                                        {...styles.collectorSelectorDynamicDropdown}
                                                                        label={translate("Sales Representative")}
                                                                        uid="salesRepDrop"
                                                                        inputId="salesRepList"
                                                                        onSelectionChange={
                                                                            this.salesPersonChangeHandler
                                                                        }
                                                                        selected={salesPerson}
                                                                        options={salesPersonOptions}
                                                                        placeholder={translate("Select Sales Person")}
                                                                        data-test-selector="productSelector_search"
                                                                        error={formErrors.salesRepList}
                                                                        required
                                                                    />
                                                                </GridItem>
                                                                <GridItem {...styles.internalFormFieldGridItem}>
                                                                    <TextField
                                                                        {...styles.userNameTextField}
                                                                        id="dunningEmailAddress"
                                                                        type="email"
                                                                        label={translate("Dunning Email Address")}
                                                                        value={dunningEmailAddress}
                                                                        data-test-selector="createNewAccount_dunningEmailAddress"
                                                                        onChange={this.dunningEmailAddHandler}
                                                                        required
                                                                        error={formErrors.dunningEmailAddress}
                                                                    />
                                                                </GridItem>
                                                                <GridItem {...styles.internalFormFieldGridItem}>
                                                                    <TextField
                                                                        {...styles.userNameTextField}
                                                                        label={translate("Number of Servers")}
                                                                        value={numberOfServers}
                                                                        onInput={(e: ChangeEvent<HTMLInputElement>) => {
                                                                            e.target.value = e.target.value.replace(
                                                                                /[^0-9]*/g,
                                                                                "",
                                                                            );
                                                                        }}
                                                                        data-test-selector="createNewAccount_dunningEmailAddress"
                                                                        onChange={this.numberofServerHandler}
                                                                    />
                                                                </GridItem>
                                                                <GridItem {...styles.internalFormFieldGridItem}>
                                                                    <TextField
                                                                        {...styles.userNameTextField}
                                                                        label={translate("Credit Spending Limit")}
                                                                        value={creditSpendingLimit}
                                                                        onInput={(e: ChangeEvent<HTMLInputElement>) => {
                                                                            e.target.value = e.target.value.replace(
                                                                                /[^0-9]*/g,
                                                                                "",
                                                                            );
                                                                        }}
                                                                        data-test-selector="createNewAccount_dunningEmailAddress"
                                                                        disabled={
                                                                            !(
                                                                                (this.props.userRole || "").indexOf(
                                                                                    "TKCCPAdmin",
                                                                                ) !== -1
                                                                            )
                                                                        }
                                                                        onChange={this.creditSpendingLimitHandler}
                                                                    />
                                                                </GridItem>
                                                                <GridItem {...styles.coffeeMakerGridItem}>
                                                                    <DatePicker
                                                                        {...styles.coffeeMakerDatePicker}
                                                                        label={translate("Coffee Maker Start Date")}
                                                                        selectedDay={coffeeMakersStartDate}
                                                                        onDayChange={this.coffeemakerHandler}
                                                                    />
                                                                </GridItem>
                                                                <GridItem {...styles.csrNotesGridItem}>
                                                                    <TextArea
                                                                        id="csrNotes"
                                                                        label={translate("CSR Notes")}
                                                                        onChange={this.csrNotesChangeHandler}
                                                                        value={csrNotes}
                                                                        data-test-selector="createAccount_csrNotes"
                                                                    />
                                                                </GridItem>
                                                                <GridItem {...styles.accountManagerGridItem}>
                                                                    <GridContainer
                                                                        {...styles.accountManagerGridContainer}
                                                                    >
                                                                        <GridItem {...styles.internalFormFieldGridItem}>
                                                                            <CheckboxGroup>
                                                                                <Checkbox
                                                                                    onChange={
                                                                                        this
                                                                                            .accManagerApprovedPropertyHandler
                                                                                    }
                                                                                    checked={accountManagerApproved}
                                                                                    data-test-selector="AccountManagerApproved"
                                                                                    disabled={
                                                                                        !displayAccApproveCheckBox
                                                                                    }
                                                                                >
                                                                                    {translate(
                                                                                        "Account Manager Approved",
                                                                                    )}
                                                                                </Checkbox>
                                                                            </CheckboxGroup>
                                                                        </GridItem>
                                                                        <GridItem {...styles.internalFormFieldGridItem}>
                                                                            <CheckboxGroup>
                                                                                <Checkbox
                                                                                    onChange={
                                                                                        this
                                                                                            .salesManagerApprovedPropertyHandler
                                                                                    }
                                                                                    checked={salesManagerApproved}
                                                                                    data-test-selector="SalesManagerApproved"
                                                                                    disabled={
                                                                                        !displayFinApproveCheckBox
                                                                                    }
                                                                                >
                                                                                    {translate(
                                                                                        "Sales Manager Approved",
                                                                                    )}
                                                                                </Checkbox>
                                                                            </CheckboxGroup>
                                                                        </GridItem>
                                                                        <GridItem {...styles.internalFormFieldGridItem}>
                                                                            <CheckboxGroup>
                                                                                <Checkbox
                                                                                    onChange={
                                                                                        this
                                                                                            .adminApprovedPropertyHandler
                                                                                    }
                                                                                    checked={adminApproved}
                                                                                    data-test-selector="AdminApproved"
                                                                                    disabled={
                                                                                        !(
                                                                                            (
                                                                                                this.props.userRole ||
                                                                                                ""
                                                                                            ).indexOf("TKCCPAdmin") !==
                                                                                            -1
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {translate(
                                                                                        "Administration  Approved",
                                                                                    )}
                                                                                </Checkbox>
                                                                            </CheckboxGroup>
                                                                        </GridItem>
                                                                    </GridContainer>
                                                                </GridItem>
                                                            </GridContainer>
                                                        </GridItem>
                                                    </GridContainer>
                                                )}
                                            </GridItem>
                                        </GridContainer>
                                        <GridContainer {...styles.submitBtnGridContainer}>
                                            <GridItem {...styles.submitGridItem}>
                                                <Button
                                                    {...styles.submitButton}
                                                    type="submit"
                                                    onClick={this.submitHandler}
                                                    data-test-selector="createAccount_createButton"
                                                    disabled={!this.displaySave()}
                                                >
                                                    {!this.state.IsCustomerExist
                                                        ? translate("Create")
                                                        : translate("Save")}
                                                </Button>
                                            </GridItem>
                                        </GridContainer>
                                    </>
                                ) : (
                                    <GridContainer {...styles.mainGridContainer}>
                                        <GridItem {...styles.nextBtnGridItem}>
                                            <StyledWrapper {...styles.msgTextWrapper}>
                                                <p>
                                                    Your Account is registered online with reference # {referenceNumber}
                                                    .
                                                </p>
                                                {siteMessage("CustomerReferenceNumber_SuccessMessage")}
                                            </StyledWrapper>
                                            <StyledWrapper>
                                                <Link
                                                    {...styles.nextButtonLink}
                                                    href={this.state.customerRegistrationUrl}
                                                >
                                                    {translate("Next >>")}
                                                </Link>
                                            </StyledWrapper>
                                        </GridItem>
                                    </GridContainer>
                                )}
                            </Tab>

                            <Tab tabKey="Documents" headline="Documents">
                                <GridContainer {...styles.mainGridContainer}>
                                    <GridItem {...styles.userGridDocumentItem}>
                                        <GridContainer {...styles.userGridContainer} className="mb-16">
                                            <GridItem {...styles.userNameGridItem}>
                                                <Typography {...styles.heading}>{translate("Documents")}</Typography>
                                            </GridItem>

                                            <GridItem {...styles.userDocumentInnerGridItem}>
                                                <Typography {...styles.requirementsTitle}>
                                                    {translate("1. Credit Application")}
                                                </Typography>
                                            </GridItem>
                                            <GridItem {...styles.userDownloadGridItem}>
                                                <Button onClick={this.downloadCreditTemplateHandler}>
                                                    {translate("Download Credit Application Template")}
                                                </Button>
                                            </GridItem>
                                            <GridItem {...styles.userFileUploadGridItem}>
                                                <FileUpload
                                                    fileName={creditApplicationFile?.name || ""}
                                                    accept=".docx,.pdf,.jpeg"
                                                    label={translate("Upload Credit Application File")}
                                                    labelPosition="top"
                                                    error={
                                                        incorrectCreditFileExtension
                                                            ? translate("must be .docx, .jpeg or .pdf")
                                                            : formErrors.creditFileError
                                                    }
                                                    onFileChange={this.creditfileHandler}
                                                    uid="creditApplication"
                                                    required
                                                />
                                                <StyledWrapper {...styles.fileNamedHint}>
                                                    <Typography as="span">
                                                        {translate(
                                                            "File Name should not contain any special characters like '%' ,'?', '&','@','#' etc.",
                                                        )}
                                                    </Typography>
                                                </StyledWrapper>
                                            </GridItem>
                                            <GridItem
                                                {...styles.userDocumentUploadBtnGridItem}
                                                className={
                                                    this.props.userName &&
                                                    displayAsperRole &&
                                                    creditApplicationFilePath &&
                                                    creditApplicationFilePath.length > 0
                                                        ? "view-btn-section"
                                                        : ""
                                                }
                                            >
                                                <Button type="button" onClick={this.uploadCreditHandler}>
                                                    {translate("Upload")}
                                                </Button>
                                                {this.props.userName &&
                                                    displayAsperRole &&
                                                    creditApplicationFilePath &&
                                                    creditApplicationFilePath.length > 0 && (
                                                        <Button type="button" onClick={this.downloadCreditHandler}>
                                                            {translate("Download")}
                                                        </Button>
                                                        // <Link  {...styles.nextButtonLink} href={creditApplicationFilePath} target="_blank">
                                                        //     {translate("Download Document")}
                                                        // </Link>
                                                    )}
                                            </GridItem>

                                            <GridItem {...styles.userDocumentInnerGridItem}>
                                                <Typography {...styles.requirementsTitle}>
                                                    {translate("2. Trade Agreement")}
                                                </Typography>
                                            </GridItem>
                                            <GridItem {...styles.userFileUploadGridItem}>
                                                <FileUpload
                                                    fileName={tradeApplicationFile?.name || ""}
                                                    accept=".docx,.pdf,.jpeg"
                                                    label={translate("Upload Trade Agreement File")}
                                                    labelPosition="top"
                                                    error={
                                                        incorrectTradeFileExtension
                                                            ? translate("must be .docx, .jpeg or .pdf")
                                                            : formErrors.tradeFileError
                                                    }
                                                    onFileChange={this.tradefileHandler}
                                                    uid="tradeAgreement"
                                                    required
                                                />
                                                <StyledWrapper {...styles.fileNamedHint}>
                                                    <Typography as="span">
                                                        {translate(
                                                            "File Name should not contain any special characters like '%' ,'?', '&','@','#' etc.",
                                                        )}
                                                    </Typography>
                                                </StyledWrapper>
                                            </GridItem>
                                            <GridItem
                                                {...styles.userDocumentUploadBtnGridItem}
                                                className={
                                                    this.props.userName &&
                                                    displayAsperRole &&
                                                    tradeAgreementFilePath &&
                                                    tradeAgreementFilePath.length > 0
                                                        ? "view-btn-section"
                                                        : ""
                                                }
                                            >
                                                <Button type="button" onClick={this.uploadTradeHandler}>
                                                    {translate("Upload")}
                                                </Button>
                                                {this.props.userName &&
                                                    displayAsperRole &&
                                                    tradeAgreementFilePath &&
                                                    tradeAgreementFilePath.length > 0 && (
                                                        <Button
                                                            type="button"
                                                            onClick={this.downloadTradeAgreementHandler}
                                                        >
                                                            {translate("Download")}
                                                        </Button>
                                                        // <Link  {...styles.nextButtonLink} href={tradeAgreementFilePath} target="_blank">
                                                        //     {translate("View Document")}
                                                        // </Link>
                                                    )}
                                            </GridItem>

                                            <GridItem {...styles.userDocumentInnerGridItem}>
                                                <Typography {...styles.requirementsTitle}>
                                                    {translate("3. Tax Certificate")}
                                                </Typography>
                                            </GridItem>
                                            <GridItem {...styles.userFileUploadGridItem}>
                                                <FileUpload
                                                    fileName={taxCertificateFile?.name || ""}
                                                    accept=".docx,.pdf,.jpeg"
                                                    label={translate("Upload Tax Certificate File")}
                                                    labelPosition="top"
                                                    error={
                                                        incorrectTaxFileExtension
                                                            ? translate("must be .docx, .jpeg or .pdf")
                                                            : formErrors.taxFileError
                                                    }
                                                    onFileChange={this.taxfileHandler}
                                                    uid="taxcertificate"
                                                    required
                                                />
                                                <StyledWrapper {...styles.fileNamedHint}>
                                                    <Typography as="span">
                                                        {translate(
                                                            "File Name should not contain any special characters like '%' ,'?', '&','@','#' etc.",
                                                        )}
                                                    </Typography>
                                                </StyledWrapper>
                                            </GridItem>
                                            <GridItem
                                                {...styles.userDocumentUploadBtnGridItem}
                                                className={
                                                    this.props.userName &&
                                                    displayAsperRole &&
                                                    taxCertificateFilePath &&
                                                    taxCertificateFilePath.length > 0
                                                        ? "view-btn-section"
                                                        : ""
                                                }
                                            >
                                                <Button type="button" onClick={this.uploadTaxHandler}>
                                                    {translate("Upload")}
                                                </Button>
                                                {this.props.userName &&
                                                    displayAsperRole &&
                                                    taxCertificateFilePath &&
                                                    taxCertificateFilePath.length > 0 && (
                                                        <Button
                                                            type="button"
                                                            onClick={this.downloadTaxCertificateHandler}
                                                        >
                                                            {translate("Download")}
                                                        </Button>
                                                        // <Link  {...styles.nextButtonLink} href={taxCertificateFilePath} target="_blank">
                                                        //     {translate("View Document")}
                                                        // </Link>
                                                    )}
                                            </GridItem>
                                        </GridContainer>
                                        {isCreditDocUploaded && isTradeDocUploaded && isTaxDocUploaded && (
                                            <StyledWrapper {...styles.documentUploadMsgWrapper}>
                                                {siteMessage("CreateCustomer_DocumentUploadMessage")}
                                            </StyledWrapper>
                                        )}
                                    </GridItem>
                                </GridContainer>
                            </Tab>
                        </TabGroup>
                    </LoadingOverlay>
                </StyledForm>
            </>
        );
    }
}
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(CreateNewAccountForm)),
    definition: {
        group: "Create Account",
        // fieldDefinitions: [],
    },
};
export default widgetModule;
